<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import RichTextEditor from '../components/RichTextEditor.vue';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
  patchMarketplaceJson,
  postMarketplaceForm,
  deleteMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

// Google Shopping (ürün taksonomisi) — kadın giyim dalından kürlenmiş öneriler.
const GOOGLE_CATEGORIES = [
  'Apparel & Accessories > Clothing > Dresses',
  'Apparel & Accessories > Clothing > Shirts & Tops',
  'Apparel & Accessories > Clothing > Skirts',
  'Apparel & Accessories > Clothing > Pants',
  'Apparel & Accessories > Clothing > Shorts',
  'Apparel & Accessories > Clothing > Activewear',
  'Apparel & Accessories > Clothing > Outerwear > Coats & Jackets',
  'Apparel & Accessories > Clothing > Sleepwear & Loungewear',
  'Apparel & Accessories > Clothing > Suits',
  'Apparel & Accessories > Clothing > Underwear & Socks',
  'Apparel & Accessories > Clothing > Swimwear',
];

const route = useRoute();
const router = useRouter();

const accountId = route.params.accountId;
const productId = computed(() => route.params.id || null);
const isEdit = computed(() => !!productId.value);

const saving = ref(false);
const deleting = ref(false);
const loading = ref(false);
const error = ref('');
// Başarı şeridi (hata şeridinin yeşil eşi) — renk ekleme gibi işlemler bildirir.
const okMsg = ref('');

// ── Temel ────────────────────────────────────────────────────────────────
const title = ref('');
const parentCode = ref('');
const handle = ref('');
const description = ref('');
const status = ref('draft');

// ── Organizasyon ───────────────────────────────────────────────────────────
const vendor = ref('Adrom Fashion');
const productType = ref('');
const googleCategory = ref('');
// Kol boyu: bos = otomatik (AI gorselden belirler), secim = elle kilit.
const sleeve = ref('');
const sleeveSource = ref('');
const SLEEVE_OPTIONS = [
  { value: '', label: 'Otomatik (AI görselden belirler)' },
  { value: 'uzun', label: 'Uzun kol (truvakar dahil)' },
  { value: 'kisa', label: 'Kısa kol' },
  { value: 'kolsuz', label: 'Kolsuz / askılı' },
  { value: 'yok', label: 'Kol yok (alt giyim)' },
];
const SLEEVE_SOURCE_LABEL = { title: 'başlıktan', ai: 'AI görselden', manual: 'elle seçildi' };
const tags = ref([]);
const tagInput = ref('');

// ── Medya ────────────────────────────────────────────────────────────────
const media = reactive({ images: [], videos: [] });
const videoInput = ref('');
const uploadingGallery = ref(false);

// (Ürün Yükleme Hattı script bloğu dosyanın sonunda — options/variants/shipping
// tanımlarından sonra gelmesi gerekiyor.)

// ── B2B Fiyatlandırma ───────────────────────────────────────────────────────
// prices: eski isimli-fiyat listesi — UI'dan kaldırıldı, veri kaybolmasın diye
// state'te taşınmaya devam eder (kaydette aynen geri yazılır).
const prices = ref([]);
// wholesaleUsd = adet başı satış fiyatı (USD) — "Tümünü uygula" ile tüm
// varyantlara yazılır; toptan siteye yönlendirilecek fiyat budur.
// costUsd = maliyet (import sırasında CostStore'dan OTOMATİK gelir).
const pricing = reactive({
  wholesaleUsd: null,
  series: '',
  compareAt: null,
  costUsd: null,
  taxRate: 10,
  // Ticimax perakende fiyatları (TL) — B2B USD alanlarından bağımsız.
  ticimaxPrice: null,
  ticimaxCompareAt: null,
  // ModaXLarge (Shopify perakende) fiyatı — BOŞ bırakılırsa Koza/Ticimax
  // fiyatı kullanılır. Yani "iki vitrinde aynı fiyat" varsayılan davranıştır.
  modaxPrice: null,
  modaxCompareAt: null,
});
const pricingOpen = ref(true);
// Maliyet ORTAK KASASI (private/maliyetler.json — Trendyol katalog paneliyle
// AYNI havuz). Trendyol'a yüklenmeyen markalı ürünlerin maliyeti buradan
// girilir (Emrah 2026-07-14: "iki seçenek de olsun").
const PANEL_KEY = '69cc189637de1fe6455ab233504606758d2c9f3a06388e84';
const costSaving = ref(false);
const costSaved = ref(false);

async function loadKasaCost() {
  if (!parentCode.value) return;
  try {
    const res = await fetch('/ticimax-api/maliyet', {
      headers: { 'X-Panel-Key': PANEL_KEY },
    });
    const data = await res.json();
    const entry = (data.maliyetler || {})[parentCode.value];
    if (entry?.maliyetUsd) pricing.costUsd = entry.maliyetUsd;
  } catch {
    // kasa okunamazsa üründeki değer kalır
  }
}

async function saveCostToKasa() {
  const usd = Number(pricing.costUsd);
  if (Number.isNaN(usd) || usd <= 0) return;
  costSaving.value = true;
  costSaved.value = false;
  try {
    const res = await fetch('/ticimax-api/maliyet', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Panel-Key': PANEL_KEY },
      body: JSON.stringify({
        stokKodu: parentCode.value,
        maliyetUsd: usd,
        urunAdi: title.value,
      }),
    });
    const data = await res.json();
    if (data.success) costSaved.value = true;
  } finally {
    costSaving.value = false;
  }
}

// ── Seri Kurucu (koli içeriği — görsel setleme) ─────────────────────────────
// Kullanıcı ham "XL (1) - 3XL (1)" metnini GÖRMEZ; bedenlere adet girer,
// kanonik metin (pricing.series) arka planda üretilir (publish bunu kullanır).
const SIZE_ORDER = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '2XL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
  '7XL',
  '8XL',
  'STD',
];
function sizeRank(sz) {
  const up = String(sz).toUpperCase().trim();
  const idx = SIZE_ORDER.indexOf(up);
  if (idx >= 0) return idx + 1000;
  const num = parseFloat(up);
  return Number.isNaN(num) ? 5000 : num;
}
const productSizes = computed(() => {
  const opt = options.value.find(o => o.name === 'Beden');
  return opt ? [...opt.values].sort((a, b) => sizeRank(a) - sizeRank(b)) : [];
});
const seriesQty = reactive({});
let seriesSyncing = false;
function seriesSyncFromText() {
  seriesSyncing = true;
  Object.keys(seriesQty).forEach(k => delete seriesQty[k]);
  const re = /([A-Za-z0-9ÇĞİÖŞÜçğıöşü]+)\s*\((\d+)\)/g;
  let m;
  while ((m = re.exec(pricing.series || ''))) {
    seriesQty[m[1].toUpperCase()] = Number(m[2]);
  }
  seriesSyncing = false;
}
function seriesTextFromQty() {
  return productSizes.value
    .filter(sz => (Number(seriesQty[String(sz).toUpperCase()]) || 0) > 0)
    .map(sz => `${sz} (${seriesQty[String(sz).toUpperCase()]})`)
    .join(' - ');
}
watch(
  seriesQty,
  () => {
    if (!seriesSyncing) pricing.series = seriesTextFromQty();
  },
  { deep: true }
);
const seriesTotalPieces = computed(() =>
  Object.values(seriesQty).reduce((a, b) => a + (Number(b) || 0), 0)
);
const seriesBoxPrice = computed(() => {
  const unit = Number(pricing.wholesaleUsd);
  if (!(unit > 0) || !(seriesTotalPieces.value > 0)) return null;
  return Math.round(unit * seriesTotalPieces.value * 100) / 100;
});
function qtyOf(sz) {
  return Number(seriesQty[String(sz).toUpperCase()]) || 0;
}
function setQty(sz, val) {
  seriesQty[String(sz).toUpperCase()] = Math.max(
    0,
    Math.floor(Number(val) || 0)
  );
}
function bumpQty(sz, d) {
  setQty(sz, qtyOf(sz) + d);
}
function seriesFillOnes() {
  productSizes.value.forEach(sz => setQty(sz, 1));
}
function seriesClear() {
  productSizes.value.forEach(sz => setQty(sz, 0));
}
// Beden başına toplam canlı stok (tüm renkler) — setleme kararına ipucu.
const stockBySize = computed(() => {
  const map = {};
  variants.value.forEach(v => {
    const sz = String((v.options || {}).Beden || '').toUpperCase();
    if (!sz) return;
    map[sz] = (map[sz] || 0) + (Number(v.stock) || 0);
  });
  return map;
});

// Canlı kâr marjı: satış vs maliyet.
const marginPct = computed(() => {
  const s = Number(pricing.wholesaleUsd);
  const c = Number(pricing.costUsd);
  if (!s || s <= 0 || !c || c <= 0) return null;
  return Math.round(((s - c) / s) * 1000) / 10;
});

// Satış fiyatını TÜM varyantlara uygula.
function applyPriceToAllVariants() {
  const p = Number(pricing.wholesaleUsd);
  if (!p || p <= 0) return;
  variants.value.forEach(v => {
    v.price = p;
  });
}

// ── Toplu Fiyatlandırma (varyant filtresiyle) ───────────────────────────────
const bulk = reactive({ price: null, pct: null, filters: {} });
const bulkFilterOptions = computed(() =>
  options.value.filter(o => o.name && o.values.length)
);
const bulkTargets = computed(() =>
  variants.value.filter(v =>
    bulkFilterOptions.value.every(o => {
      const sel = bulk.filters[o.name];
      return !sel || (v.options || {})[o.name] === sel;
    })
  )
);
function applyBulkPrice() {
  const p = Number(bulk.price);
  if (!p || p <= 0) return;
  bulkTargets.value.forEach(v => {
    v.price = p;
  });
}
function applyBulkPct() {
  const pct = Number(bulk.pct);
  if (!pct) return;
  bulkTargets.value.forEach(v => {
    const cur = Number(v.price);
    if (cur > 0) v.price = Math.round(cur * (1 + pct / 100) * 100) / 100;
  });
}

// ── Koleksiyonlar (çok seçimli — manuel koleksiyonlara üye yapar) ──────────
const allCollections = ref([]);
const collectionIds = ref([]);
const collectionsOpen = ref(false);
const manualCollections = computed(() =>
  allCollections.value.filter(c => c.collectionType !== 'smart')
);
const smartCollections = computed(() =>
  allCollections.value.filter(c => c.collectionType === 'smart')
);
function toggleCollection(id) {
  const i = collectionIds.value.indexOf(id);
  if (i >= 0) collectionIds.value.splice(i, 1);
  else collectionIds.value.push(id);
}
async function loadCollections() {
  try {
    const data = await fetchMarketplaceJson(accountId, 'collections');
    allCollections.value = data.collections || [];
  } catch {
    allCollections.value = [];
  }
}

// ── Varyantlar ───────────────────────────────────────────────────────────
const options = ref([]); // [{ name, values:[], valueInput }]
const variants = ref([]);

// Select'ler seçimsiz kalmasın: her seçenek için varsayılan '' (tümü).
watch(
  bulkFilterOptions,
  opts => {
    opts.forEach(o => {
      if (bulk.filters[o.name] === undefined) bulk.filters[o.name] = '';
    });
  },
  { immediate: true, deep: true }
);

// Varyant görseli seçici (popup galeri)
const picker = reactive({ open: false, variant: null });
const pickerUploading = ref(false);
// Seçici içi arama — bir üründe 100+ görsel olabiliyor, filtre şart.
const pickerQuery = ref('');
const pickerImages = computed(() => {
  const q = pickerQuery.value.trim().toLowerCase();
  if (!q) return media.images;
  return media.images.filter(u => u.toLowerCase().includes(q));
});

// Galeri: küçük görselleri kısalt (+N ile aç)
const THUMB_LIMIT = 5;
const showAllImages = ref(false);
const restImages = computed(() => media.images.slice(1));
const thumbImages = computed(() =>
  showAllImages.value
    ? restImages.value
    : restImages.value.slice(0, THUMB_LIMIT)
);
const hiddenImageCount = computed(() =>
  Math.max(0, restImages.value.length - THUMB_LIMIT)
);

// Varyantlar: ilk seçeneğe göre gruplu akordiyon (2+ seçenek olduğunda)
const expandedGroups = ref(new Set());
const primaryOption = computed(() => {
  const opts = options.value.filter(o => o.name && o.values.length);
  return opts.length >= 2 ? opts[0].name : null;
});
const isGrouped = computed(() => !!primaryOption.value);
const variantGroups = computed(() => {
  if (!primaryOption.value) return [];
  const map = new Map();
  variants.value.forEach(v => {
    const k = v.options[primaryOption.value] || '—';
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(v);
  });
  return [...map.entries()].map(([label, vs]) => ({
    key: label,
    label,
    variants: vs,
  }));
});
function toggleGroup(key) {
  const s = new Set(expandedGroups.value);
  if (s.has(key)) s.delete(key);
  else s.add(key);
  expandedGroups.value = s;
}
function groupStock(group) {
  return group.variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
}
function groupImage(group) {
  const withImg = group.variants.find(v => v.image);
  return withImg ? withImg.image : media.images[0] || '';
}
function subLabel(v) {
  return (
    Object.entries(v.options || {})
      .filter(([k]) => k !== primaryOption.value)
      .map(([, val]) => val)
      .join(' / ') || v.key
  );
}

// ── SEO ────────────────────────────────────────────────────────────────────
const seo = reactive({ title: '', description: '', handle: '' });

// ── Kargo ───────────────────────────────────────────────────────────────────
// Boyutlar girilirse desi otomatik hesaplanır (E×B×Y / 3000); elle değer
// girilirse (desiManual) otomatik hesap devre dışı kalır.
const shipping = reactive({
  weight: null,
  weightUnit: 'kg',
  width: null,
  length: null,
  height: null,
  desi: null,
  desiManual: false,
  hsCode: '',
  originCountry: 'TR',
  prepDays: null,
});
const autoDesi = computed(() => {
  const { width: w, length: l, height: h } = shipping;
  if (!w || !l || !h) return null;
  return Math.round(((w * l * h) / 3000) * 100) / 100;
});
watch(autoDesi, val => {
  if (val !== null && !shipping.desiManual) shipping.desi = val;
});
function onDesiInput() {
  shipping.desiManual = true;
}
function resetDesiAuto() {
  shipping.desiManual = false;
  if (autoDesi.value !== null) shipping.desi = autoDesi.value;
}

// ── Yardımcılar ─────────────────────────────────────────────────────────────
function slugify(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function onTitleBlur() {
  if (!handle.value) handle.value = slugify(title.value);
  if (!seo.handle) seo.handle = handle.value || slugify(title.value);
  if (!seo.title) seo.title = title.value;
}

// Etiket
function addTag() {
  const v = tagInput.value.trim();
  if (v && !tags.value.includes(v)) tags.value.push(v);
  tagInput.value = '';
}
function removeTag(t) {
  tags.value = tags.value.filter(x => x !== t);
}

// Medya yükleme (galeri + varyant + video için ortak)
async function uploadFiles(fileList, onUrl, flag) {
  const files = Array.from(fileList || []);
  if (!files.length) return;
  if (flag) flag.value = true;
  try {
    await Promise.all(
      files.map(async file => {
        const fd = new FormData();
        fd.append('file', file);
        const res = await postMarketplaceForm(
          accountId,
          'product_center/upload',
          fd
        );
        if (res.url) onUrl(res.url);
      })
    );
  } catch (e) {
    error.value = 'Yükleme hatası: ' + e.message;
  } finally {
    if (flag) flag.value = false;
  }
}

function onGalleryPick(e) {
  uploadFiles(e.target.files, url => media.images.push(url), uploadingGallery);
  e.target.value = '';
}
function onGalleryDrop(e) {
  uploadFiles(
    e.dataTransfer.files,
    url => media.images.push(url),
    uploadingGallery
  );
}
function removeImage(url) {
  media.images = media.images.filter(x => x !== url);
}
function makeCover(url) {
  media.images = [url, ...media.images.filter(x => x !== url)];
}

function addVideoUrl() {
  const v = videoInput.value.trim();
  if (v && !media.videos.includes(v)) media.videos.push(v);
  videoInput.value = '';
}
function onVideoPick(e) {
  uploadFiles(e.target.files, url => media.videos.push(url));
  e.target.value = '';
}
function removeVideo(url) {
  media.videos = media.videos.filter(x => x !== url);
}

// Seçenekler + varyantlar
function buildVariants() {
  const opts = options.value.filter(o => o.name && o.values.length);
  let combos = [[]];
  opts.forEach(o => {
    const next = [];
    combos.forEach(c => {
      o.values.forEach(val => next.push([...c, { name: o.name, value: val }]));
    });
    combos = next;
  });
  const existing = {};
  variants.value.forEach(v => {
    existing[v.key] = v;
  });
  variants.value = combos.map(combo => {
    const key = combo.map(c => c.value).join(' / ') || 'Varsayılan';
    const optionMap = {};
    combo.forEach(c => {
      optionMap[c.name] = c.value;
    });
    const prev = existing[key] || {};
    return {
      key,
      options: optionMap,
      sku: prev.sku || '',
      barcode: prev.barcode || '',
      price: prev.price ?? null,
      costUsd: prev.costUsd ?? null,
      stock: prev.stock ?? 0,
      image: prev.image || '',
    };
  });
}

function addOption() {
  if (options.value.length >= 3) return;
  options.value.push({ name: '', values: [], valueInput: '' });
}
function removeOption(idx) {
  options.value.splice(idx, 1);
  buildVariants();
}
// Renk Sözlüğü (tek doğruluk kaynağı) — Renk seçeneğine serbest metin girilemez;
// sözlükte olmayan ad için kullanıcı açıkça onaylar (sözlüğe de eklenir).
const colorDict = ref([]);
async function loadColorDict() {
  try {
    const data = await fetchMarketplaceJson(accountId, 'product_center/colors');
    colorDict.value = data.colors || [];
  } catch {
    colorDict.value = [];
  }
}
function dictLookup(name) {
  const n = name.toLocaleLowerCase('tr').trim();
  return colorDict.value.find(
    c =>
      c.name.toLocaleLowerCase('tr') === n ||
      (c.aliases || []).some(a => a.toLocaleLowerCase('tr') === n)
  );
}

function addOptionValue(opt) {
  let v = (opt.valueInput || '').trim();
  if (!v) return;
  if (opt.name === 'Renk') {
    const hit = dictLookup(v);
    if (hit) {
      v = hit.name; // takma ad yazıldıysa kanonik ada çevir (Pink → Pembe)
    } else {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `"${v}" Renk Sözlüğü'nde yok. Yazım hatası olabilir mi?\n\n` +
          'Tamam dersen sözlüğe YENİ RENK olarak eklenecek ve kullanılacak.'
      );
      if (!ok) return;
      postMarketplaceJsonBody(accountId, 'product_center/colors', {
        name: v,
      }).then(loadColorDict);
    }
  }
  if (v && !opt.values.includes(v)) opt.values.push(v);
  opt.valueInput = '';
  buildVariants();
}
function removeOptionValue(opt, val) {
  opt.values = opt.values.filter(x => x !== val);
  buildVariants();
}

// Varyant görseli popup galeri
function openPicker(variant) {
  picker.variant = variant;
  picker.open = true;
  pickerQuery.value = '';
}
function closePicker() {
  picker.open = false;
  picker.variant = null;
}
function chooseVariantImage(url) {
  if (picker.variant) picker.variant.image = url;
  closePicker();
}
function clearVariantImage() {
  if (picker.variant) picker.variant.image = '';
  closePicker();
}
function onPickerUpload(e) {
  const target = picker.variant;
  uploadFiles(
    e.target.files,
    url => {
      if (!media.images.includes(url)) media.images.push(url);
      if (target) target.image = url;
    },
    pickerUploading
  );
  e.target.value = '';
}

// ── Kaydet / Sil ─────────────────────────────────────────────────────────
async function save({ stay = false } = {}) {
  if (!title.value.trim()) {
    error.value = 'Ürün başlığı zorunlu.';
    return;
  }
  saving.value = true;
  error.value = '';
  const payload = {
    title: title.value,
    parentCode: parentCode.value,
    handle: handle.value || slugify(title.value),
    description: description.value,
    status: status.value,
    vendor: vendor.value,
    productType: productType.value,
    googleCategory: googleCategory.value,
    sleeve: sleeve.value,
    tags: tags.value,
    media: {
      images: media.images,
      videos: media.videos,
      colorImages: { ...colorImages },
    },
    options: options.value.map(o => ({ name: o.name, values: o.values })),
    variants: variants.value,
    prices: prices.value.filter(p => p.name || p.amount),
    pricing: { ...pricing },
    seo: { ...seo },
    shipping: { ...shipping },
    collectionIds: collectionIds.value,
  };
  try {
    if (isEdit.value) {
      await patchMarketplaceJson(
        accountId,
        `product_center/${productId.value}`,
        {
          product: payload,
        }
      );
    } else {
      await postMarketplaceJsonBody(accountId, 'product_center', {
        product: payload,
      });
    }
    if (!stay)
      router.push({ name: 'product_center_index', params: { accountId } });
  } catch (e) {
    error.value = 'Kaydedilemedi: ' + e.message;
    if (stay) throw e;
  } finally {
    saving.value = false;
  }
}

async function deleteProduct() {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Bu ürün kalıcı olarak silinsin mi?')) return;
  deleting.value = true;
  try {
    await deleteMarketplaceJson(accountId, `product_center/${productId.value}`);
    router.push({ name: 'product_center_index', params: { accountId } });
  } catch (e) {
    error.value = 'Silinemedi: ' + e.message;
    deleting.value = false;
  }
}

function cancel() {
  router.push({ name: 'product_center_index', params: { accountId } });
}

async function loadExisting() {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const { product: p } = await fetchMarketplaceJson(
      accountId,
      `product_center/${productId.value}`
    );
    title.value = p.title || '';
    parentCode.value = p.parentCode || '';
    loadKasaCost();
    handle.value = p.handle || '';
    description.value = p.description || '';
    status.value = p.status || 'draft';
    vendor.value = p.vendor || '';
    productType.value = p.productType || '';
    googleCategory.value = p.googleCategory || '';
    sleeve.value = p.sleeve || '';
    sleeveSource.value = p.sleeveSource || '';
    tags.value = p.tags || [];
    media.images = (p.media && p.media.images) || [];
    media.videos = (p.media && p.media.videos) || [];
    Object.keys(colorImages).forEach(k => delete colorImages[k]);
    Object.assign(colorImages, (p.media && p.media.colorImages) || {});
    options.value = (p.options || []).map(o => ({ ...o, valueInput: '' }));
    variants.value = p.variants || [];
    prices.value = p.prices || [];
    Object.assign(pricing, p.pricing || {});
    seriesSyncFromText();
    Object.assign(seo, p.seo || {});
    Object.assign(shipping, p.shipping || {});
    collectionIds.value = p.collectionIds || [];
  } catch (e) {
    error.value = 'Ürün yüklenemedi: ' + e.message;
  } finally {
    loading.value = false;
  }
}

// ── Satış Kanalları (Entegrasyon Merkezi bağ durumu) ────────────────────────
const channelCards = ref([]);
const channelsLoading = ref(false);
async function loadChannelStatus() {
  if (!isEdit.value) return;
  channelsLoading.value = true;
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `channels/product_status?productId=${productId.value}`
    );
    channelCards.value = data.channels || [];
    // HER Shopify kanalı kendi koleksiyonlarını yükler.
    channelCards.value
      .filter(c => c.channelType.startsWith('shopify'))
      .forEach(c => loadShopCollections(c.channelId));
  } catch {
    channelCards.value = [];
  } finally {
    channelsLoading.value = false;
  }
}

// ── Koleksiyon Köprüsü: önizleme (smart) + aramalı çoklu seçim (manuel) ─────
// HER KANALIN KENDI koleksiyonlari. Tek ortak listede tutulunca ModaXLarge
// kartinin altinda Adrom'un toptan koleksiyonlari goruluyordu (4 Eyl 2026).
// Yapi: collectionsBy[channelId] = { list, selected, suggested, initial, source, search, applying, result }
const collectionsBy = reactive({});

const bosKoleksiyon = () => ({
  list: [],
  selected: [],
  suggested: [],
  initial: [],
  source: 'auto',
  search: '',
  applying: false,
  result: '',
});

function collectionStateFor(channelId) {
  if (!collectionsBy[channelId]) collectionsBy[channelId] = bosKoleksiyon();
  return collectionsBy[channelId];
}

async function loadShopCollections(channelId) {
  const st = collectionStateFor(channelId);
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `channels/${channelId}/collections?productId=${productId.value}`
    );
    st.list = data.collections || [];
    st.suggested = data.suggestions || [];
    // Backend seçim önceliğini çözer: kullanıcı kaydı > bağ > akıllı öneri.
    st.selected = [...(data.selected || [])];
    st.initial = [...(data.selected || [])];
    st.source = data.selectedSource || 'auto';
  } catch {
    st.list = [];
    st.selected = [];
  }
}

// Aşağıdaki yardımcıların HEPSİ channelId alır — iki kanalın koleksiyon
// seçimi birbirine karışmasın diye (Adrom toptan koleksiyonları ≠ ModaXLarge
// perakende koleksiyonları).
const collectionSearchResults = channelId => {
  const st = collectionStateFor(channelId);
  const q = st.search.toLocaleLowerCase('tr-TR').trim();
  if (!q) return [];
  return st.list
    .filter(
      c =>
        c.title.toLocaleLowerCase('tr-TR').includes(q) &&
        !st.selected.includes(c.id)
    )
    .slice(0, 8);
};
const collectionsDirty = channelId => {
  const st = collectionStateFor(channelId);
  return [...st.selected].sort().join(',') !== [...st.initial].sort().join(',');
};
function addCollection(channelId, id) {
  const st = collectionStateFor(channelId);
  if (!st.selected.includes(id)) st.selected.push(id);
  st.search = '';
  st.result = '';
}
function removeCollection(channelId, id) {
  const st = collectionStateFor(channelId);
  st.selected = st.selected.filter(x => x !== id);
  st.result = '';
}
const collectionTitle = (channelId, id) =>
  collectionStateFor(channelId).list.find(c => c.id === id)?.title || id;
const collectionSelectable = (channelId, id) =>
  collectionStateFor(channelId).list.find(c => c.id === id)?.selectable !==
  false;

// "Koleksiyonları Güncelle": seçim ürüne kalıcı kaydedilir; ürün kanalda
// yayımlıysa anında Shopify'a uygulanır (kilitli akıllılar rapor edilir).
async function applyCollections(ch) {
  const st = collectionStateFor(ch.channelId);
  st.applying = true;
  st.result = '';
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      `channels/${ch.channelId}/apply_collections`,
      { productId: productId.value, collectionIds: st.selected }
    );
    st.initial = [...(res.selected || [])];
    st.source = 'user';
    const parts = [];
    parts.push(
      res.applied
        ? 'Koleksiyonlar Shopify’a uygulandı.'
        : 'Seçim kaydedildi — ürün yayınlanınca uygulanacak.'
    );
    if ((res.skipped || []).length)
      parts.push(
        `${res.skipped.length} akıllı koleksiyon kural devrini bekliyor (başlık kuralıyla çalışmaya devam eder).`
      );
    st.result = '✓ ' + parts.join(' ');
  } catch (e) {
    st.result = '⚠ Uygulanamadı: ' + e.message;
  } finally {
    st.applying = false;
  }
}

// Kanal başına ayrı durum: iki kanal kartı aynı ref'i paylaşınca birinde
// yayınlarken diğerinin düğmesi de kilitleniyor ve sonuç iki kartta birden
// görünüyordu (4 Eyl 2026). Artık her şey channelId ile anahtarlı.
const publishingBy = reactive({});
const publishResultBy = reactive({});
const publishActiveBy = reactive({});
const activatingBy = reactive({});
async function publishToChannel(ch) {
  publishingBy[ch.channelId] = true;
  publishResultBy[ch.channelId] = '';
  error.value = '';
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      `channels/${ch.channelId}/publish`,
      {
        productId: productId.value,
        activate: !!publishActiveBy[ch.channelId],
        collectionIds: collectionStateFor(ch.channelId).selected,
      }
    );
    publishResultBy[ch.channelId] = res.draft
      ? `Taslak oluşturuldu (${res.link.variantCount} varyant). İncele → Vitrine Aç.`
      : `Yayınlandı! Ürün vitrinde (${res.link.variantCount} varyant).`;
    await loadChannelStatus();
  } catch (e) {
    error.value = 'Yayınlanamadı: ' + e.message;
  } finally {
    publishingBy[ch.channelId] = false;
  }
}
async function activateOnChannel(ch) {
  activatingBy[ch.channelId] = true;
  error.value = '';
  try {
    await postMarketplaceJsonBody(
      accountId,
      `channels/${ch.channelId}/activate`,
      { productId: productId.value }
    );
    publishResultBy[ch.channelId] = 'Vitrine açıldı — ürün Online Store’da yayında.';
    await loadChannelStatus();
  } catch (e) {
    error.value = 'Vitrine açılamadı: ' + e.message;
  } finally {
    activatingBy[ch.channelId] = false;
  }
}

// ── Ürün Yükleme Hattı: Renk Stüdyosu + Gören AI (2026-07-29) ──────────────
// colorImages: { 'Sarı': [url...], 'belirsiz': [url...] } — varyanta özel galeri.
const colorImages = reactive({});
const aiClassifying = ref(false);
const classifyProgress = ref('');
const aiWriting = ref(false);
const draggingUrl = ref('');

const productColors = computed(() => {
  const opt = options.value.find(o => o.name === 'Renk');
  return opt ? opt.values : [];
});
const studioGroups = computed(() =>
  [...productColors.value, 'belirsiz'].map(c => ({
    color: c,
    images: colorImages[c] || [],
  }))
);
const assignedUrls = computed(() => new Set(Object.values(colorImages).flat()));
const unassignedImages = computed(() =>
  media.images.filter(u => !assignedUrls.value.has(u))
);

// Renk galerisinin ilk görseli o rengin varyantlarına kapak olur.
function autoAssignVariantImages() {
  variants.value.forEach(v => {
    const imgs = colorImages[(v.options || {}).Renk];
    if (imgs && imgs.length) v.image = imgs[0];
  });
}

// Toplu galeriyi AI ile renklere ayrıştır (8'lik parçalar — istek zaman aşımı olmasın).
async function classifyGallery() {
  if (!productColors.value.length || !unassignedImages.value.length) return;
  aiClassifying.value = true;
  try {
    const urls = [...unassignedImages.value];
    for (let i = 0; i < urls.length; i += 8) {
      classifyProgress.value = `${Math.min(i + 8, urls.length)}/${urls.length}`;
      // eslint-disable-next-line no-await-in-loop
      const data = await postMarketplaceJsonBody(
        accountId,
        'product_center/classify_images',
        { urls: urls.slice(i, i + 8), colors: productColors.value }
      );
      Object.entries(data.results || {}).forEach(([url, res]) => {
        const c = res && res.color ? res.color : 'belirsiz';
        if (!colorImages[c]) colorImages[c] = [];
        if (!colorImages[c].includes(url)) colorImages[c].push(url);
      });
    }
    autoAssignVariantImages();
  } catch (e) {
    error.value = 'Renk ayrıştırma hatası: ' + e.message;
  } finally {
    aiClassifying.value = false;
    classifyProgress.value = '';
  }
}

function moveImageToColor(url, color) {
  Object.keys(colorImages).forEach(c => {
    colorImages[c] = colorImages[c].filter(u => u !== url);
  });
  if (!colorImages[color]) colorImages[color] = [];
  colorImages[color].push(url);
  autoAssignVariantImages();
}

// Renk galerisine doğrudan yükleme. Yüklenen görsel hem ürün galerisine hem
// o rengin galerisine girer; varyant kapağı da güncellenir.
function studyoGorselYukle(e, color) {
  const files = e.target.files;
  e.target.value = '';
  if (!files || !files.length) return;
  renkYukleniyor.value = color;
  uploadFiles(
    files,
    url => {
      if (!media.images.includes(url)) media.images.push(url);
      if (!colorImages[color]) colorImages[color] = [];
      if (!colorImages[color].includes(url)) colorImages[color].push(url);
      autoAssignVariantImages();
    },
    { set value(v) { renkYukleniyor.value = v ? color : ''; } }
  );
}

function onStudioDrop(color) {
  if (draggingUrl.value) moveImageToColor(draggingUrl.value, color);
  draggingUrl.value = '';
}

// Gören AI: fotoğraflara bakarak marka dilinde ad + açıklama üretir.
async function generateCopy() {
  if (!isEdit.value || !media.images.length) {
    error.value = 'Önce ürünü kaydedip görsel yükleyin.';
    return;
  }
  aiWriting.value = true;
  error.value = '';
  try {
    const data = await postMarketplaceJsonBody(
      accountId,
      'product_center/generate_copy',
      { id: productId.value, urls: media.images.slice(0, 3) }
    );
    if (data.title) title.value = data.title;
    if (data.description) description.value = data.description;
  } catch (e) {
    error.value = 'AI üretemedi: ' + e.message;
  } finally {
    aiWriting.value = false;
  }
}

// ── Ticimax'e Gönder (pasif — Prapazar oradan import eder) ─────────────────
const ticimax = reactive({
  open: false,
  cats: [],
  catQuery: '',
  catIds: [],
  suggested: '',
  sending: false,
  msg: '',
});

function toggleTicimaxCat(id) {
  const i = ticimax.catIds.indexOf(id);
  if (i >= 0) ticimax.catIds.splice(i, 1);
  else ticimax.catIds.push(id);
}

// Kategorinin ÜST menü zinciri (parentId ile köke kadar) — üst kategoriler de
// işaretlenir ki ürün menünün her seviyesinde görünsün.
function catAncestors(id) {
  const byId = new Map(ticimax.cats.map(c => [c.id, c]));
  const chain = [];
  let cur = byId.get(id);
  let guard = 0;
  while (cur && cur.parentId && guard < 10) {
    guard += 1;
    cur = byId.get(cur.parentId);
    if (cur) chain.push(cur.id);
  }
  return chain;
}

// Akıllı çoklu öneri: tip eşleşen yaprak kategori + TÜM üst menüleri +
// adında "yeni" geçen kategoriler (Yeni Ürünler/Yeni Gelenler) otomatik.
function suggestTicimaxCategory() {
  const tr = s => (s || '').toLocaleLowerCase('tr');
  const words = tr(productType.value)
    .split(/[\s/]+/)
    .filter(w => w.length > 2);
  const active = ticimax.cats.filter(c => c.aktif !== false);
  let best = null;
  let bestScore = 0;
  if (words.length) {
    active.forEach(c => {
      const ad = tr(c.ad);
      let score = words.filter(w => ad.includes(w)).length;
      if (score && ad.includes('büyük beden')) score += 0.5;
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    });
  }
  const ids = [];
  if (best) {
    ids.push(best.id, ...catAncestors(best.id));
    ticimax.suggested = best.ad;
  }
  active.forEach(c => {
    if (tr(c.ad).includes('yeni') && !ids.includes(c.id)) ids.push(c.id);
  });
  ticimax.catIds = ids;
}
const ticimaxId = computed(() => (shipping || {}).ticimaxId);
const filteredCats = computed(() => {
  const q = ticimax.catQuery.trim().toLowerCase();
  const list = ticimax.cats.filter(c => c.aktif !== false);
  return q ? list.filter(c => (c.ad || '').toLowerCase().includes(q)) : list;
});
async function openTicimax() {
  ticimax.open = true;
  ticimax.msg = '';
  ticimax.suggested = '';
  ticimax.catIds = Array((shipping || {}).ticimaxCategoryIds || []).map(Number);
  if (!ticimax.cats.length) {
    try {
      const data = await fetchMarketplaceJson(
        accountId,
        'product_center/ticimax_categories'
      );
      ticimax.cats = data.kategoriler || [];
    } catch (e) {
      ticimax.msg = 'Kategoriler alınamadı: ' + e.message;
    }
  }
  if (!ticimax.catIds.length) suggestTicimaxCategory();
}
async function sendToTicimax() {
  if (!ticimax.catIds.length) {
    ticimax.msg = 'En az bir kategori seçin.';
    return;
  }
  if (!(Number(pricing.ticimaxPrice) > 0)) {
    ticimax.msg =
      'Ticimax satış fiyatı (TL) boş — sağdaki Ticimax bloğundan Fiyat Hesapla ya da elle girin.';
    return;
  }
  ticimax.sending = true;
  ticimax.msg = '';
  try {
    await save({ stay: true });
    const data = await postMarketplaceJsonBody(
      accountId,
      'product_center/push_ticimax',
      { id: productId.value, categoryIds: ticimax.catIds }
    );
    Object.assign(shipping, (data.product && data.product.shipping) || {});
    // KRİTİK: dönen varyantlar (ticimaxVariantId eşlemeleriyle) form state'ine
    // yazılır — yoksa sonraki Kaydet eşlemeyi siler, güncelleme çift varyant atar
    // (2026-07-30 canlı test dersi).
    if (data.product && data.product.variants)
      variants.value = data.product.variants;
    ticimax.msg = `✅ Gönderildi — Ticimax ürün no ${data.ticimaxId} (pasif, yayında değil)`;
  } catch (e) {
    ticimax.msg = 'Gönderilemedi: ' + e.message;
  } finally {
    ticimax.sending = false;
  }
}

// ModaXLarge'a fiili gidecek fiyat: kendi alanı doluysa o, değilse Koza fiyatı.
// Backend'deki retail_pricing ile AYNI kural — ekran ne gösteriyorsa o gider.
const modaxEffectivePrice = computed(
  () => pricing.modaxPrice || pricing.ticimaxPrice || null
);
const modaxEffectiveCompareAt = computed(
  () => pricing.modaxCompareAt || pricing.ticimaxCompareAt || null
);

// ── Ticimax Fiyat Hesapla (SKU Kâr Motoru — site vitrin bandı) ─────────────
const priceCalc = reactive({ busy: false, info: '', quotes: null });
async function calcTicimaxPrice() {
  const usd = Number(pricing.costUsd);
  if (!(usd > 0)) {
    priceCalc.info =
      'Önce maliyet ($) girin — kasadan otomatik gelir ya da elle yazın.';
    return;
  }
  priceCalc.busy = true;
  priceCalc.info = '';
  try {
    const data = await postMarketplaceJsonBody(
      accountId,
      'sku_profits/calculate',
      {
        unit_cost: usd,
      }
    );
    const site = (data.quotes || []).find(q => q.type === 'site');
    const ty = (data.quotes || []).find(q => q.type === 'pazaryeri');
    if (site && site.list_price) {
      pricing.ticimaxPrice = site.list_price;
      pricing.ticimaxCompareAt = ty && ty.list_price ? ty.list_price : null;
      priceCalc.quotes = { site, ty };
      priceCalc.info = `Motor önerisi uygulandı (kur ${data.usd_rate} · maliyet ${data.unit_cost} TL KDV'li). Elle değiştirebilirsin.`;
    } else {
      priceCalc.info =
        'Motor fiyat üretemedi — Finans > Ürün Fiyat & İade ekranından motoru yenileyin.';
    }
  } catch (e) {
    priceCalc.info = 'Hesaplanamadı: ' + e.message;
  } finally {
    priceCalc.busy = false;
  }
}

// ── 📥 Excel'den Renk Ekle (2026-09-15) ────────────────────────────────────
// Adrom'da ürüne renk eklenince export TÜM renkleri getirir. Aynı Excel buraya
// yüklenir; sistem farkı çıkarır (kartta olmayan varyantlar) ve onaydan sonra
// SADECE eksikleri ekler. Mevcut varyantlara, barkodlara ve kanal kimliklerine
// dokunulmaz — kartta olup Excel'de olmayan varyant da SİLİNMEZ.
const renkEkleAcik = ref(false);
const renkEkleFark = ref(null);
const renkEkleDosya = ref(null);
const renkEkleMesgul = ref(false);
const renkEkleHata = ref('');
// Adrom'un Nebim'i ürettiği TÜM renkleri export eder; Koza her rengi almadığı
// için kullanıcı hangilerini istediğini burada işaretler. Varsayılan: hiçbiri.
const secilenRenkler = ref([]);

const renkOzetleri = computed(() => {
  const f = renkEkleFark.value;
  if (!f) return [];
  return f.yeniRenkOzeti || f.yeni_renk_ozeti || [];
});
const secilenVaryantSayisi = computed(() =>
  renkOzetleri.value
    .filter(r => secilenRenkler.value.includes(r.renk))
    .reduce((t, r) => t + r.adet, 0)
);

// Seçilen renklerin görselleri: { 'Zümrüt Yeşili': [url, ...] }. Kanaldan
// çekilen ürünlerde galeri renk renk ayrıştırılmış değil; yeni rengin
// fotoğraflarını burada, renk bazında yüklemek en temizi.
const renkGorselleri = reactive({});
const renkYukleniyor = ref('');

function renkSec(renk) {
  const i = secilenRenkler.value.indexOf(renk);
  if (i >= 0) {
    secilenRenkler.value.splice(i, 1);
    delete renkGorselleri[renk];
  } else {
    secilenRenkler.value.push(renk);
    if (!renkGorselleri[renk]) renkGorselleri[renk] = [];
  }
}

function renkGorselSec(e, renk) {
  const files = e.target.files;
  e.target.value = '';
  if (!files || !files.length) return;
  if (!renkGorselleri[renk]) renkGorselleri[renk] = [];
  renkYukleniyor.value = renk;
  uploadFiles(
    files,
    url => {
      if (!renkGorselleri[renk].includes(url)) renkGorselleri[renk].push(url);
    },
    { set value(v) { renkYukleniyor.value = v ? renk : ''; } }
  );
}

function renkGorselSil(renk, url) {
  renkGorselleri[renk] = (renkGorselleri[renk] || []).filter(u => u !== url);
}

const gorselsizSecili = computed(() =>
  secilenRenkler.value.filter(r => !(renkGorselleri[r] || []).length)
);
function tumunuSec() {
  secilenRenkler.value = renkOzetleri.value.map(r => r.renk);
  secilenRenkler.value.forEach(r => {
    if (!renkGorselleri[r]) renkGorselleri[r] = [];
  });
}
function secimiTemizle() {
  secilenRenkler.value = [];
  Object.keys(renkGorselleri).forEach(k => delete renkGorselleri[k]);
}

// Renk adından örnek nokta rengi — sadece görsel ipucu, kararı kullanıcı verir.
const RENK_NOKTA = {
  siyah: '#1c1917', beyaz: '#fafafa', ekru: '#f0e6d2', krem: '#f5efe0',
  kirmizi: '#dc2626', bordo: '#7f1d1d', pembe: '#ec4899', pudra: '#f3d6d1',
  mavi: '#2563eb', lacivert: '#1e3a5f', 'saks mavi': '#1d4ed8', turkuaz: '#14b8a6',
  yesil: '#16a34a', 'zumrut yesili': '#059669', haki: '#6b7250', mint: '#a7f3d0',
  kahverengi: '#6b4423', bej: '#d4c5a9', 'koyu bej': '#c4ab82', vizon: '#a89484',
  tas: '#b8ad9a', sari: '#eab308', hardal: '#ca8a04', turuncu: '#f97316',
  mor: '#9333ea', lila: '#c4b5fd', gri: '#6b7280', fume: '#4b5563',
  antrasit: '#374151', gumus: '#c0c0c0', somon: '#fa8072',
};
function renkNoktasi(ad) {
  const k = (ad || '')
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .trim();
  return RENK_NOKTA[k] || '#cbd5e1';
}

async function renkEkleDosyaSecildi(e) {
  const file = e.target.files && e.target.files[0];
  e.target.value = '';
  if (!file) return;
  renkEkleDosya.value = file;
  renkEkleFark.value = null;
  renkEkleHata.value = '';
  secilenRenkler.value = [];
  Object.keys(renkGorselleri).forEach(k => delete renkGorselleri[k]);
  renkEkleMesgul.value = true;
  renkEkleAcik.value = true;
  try {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('id', productId.value);
    renkEkleFark.value = await postMarketplaceForm(
      accountId,
      'product_center/diff_excel',
      fd
    );
  } catch (err) {
    renkEkleHata.value = err.message || 'Excel okunamadı.';
  } finally {
    renkEkleMesgul.value = false;
  }
}

// Onay: eksik varyantlar karta eklenir, sonra ürün yeniden yüklenir —
// yeni renk Renk Stüdyosu'nda kendiliğinden belirir, görselleri oraya sürüklenir.
async function renkEkleUygula() {
  if (!renkEkleDosya.value || !renkEkleFark.value) return;
  renkEkleMesgul.value = true;
  renkEkleHata.value = '';
  try {
    const fd = new FormData();
    fd.append('file', renkEkleDosya.value);
    fd.append('id', productId.value);
    secilenRenkler.value.forEach(r => {
      fd.append('colors[]', r);
      (renkGorselleri[r] || []).forEach(u =>
        fd.append(`colorImages[${r}][]`, u)
      );
    });
    const data = await postMarketplaceForm(
      accountId,
      'product_center/merge_excel',
      fd
    );
    await loadExisting();
    renkEkleAcik.value = false;
    renkEkleDosya.value = null;
    renkEkleFark.value = null;
    secilenRenkler.value = [];
    Object.keys(renkGorselleri).forEach(k => delete renkGorselleri[k]);
    okMsg.value =
      `${data.eklenen} varyant eklendi (${(data.eklenenRenkler || data.eklenen_renkler || []).join(', ')}). ` +
      'Görsellerini Renk Stüdyosu\'ndan atayıp kaydedin.';
  } catch (err) {
    renkEkleHata.value = err.message || 'Eklenemedi.';
  } finally {
    renkEkleMesgul.value = false;
  }
}

// ── ⚡ Hızlı Stok Matrisi (satır=renk, sütun=beden) ─────────────────────────
const stokM = reactive({ open: false, qty: {} });
function stokKey(renk, beden) {
  return `${renk}|${beden}`;
}
function openStokMatrix() {
  stokM.qty = {};
  variants.value.forEach(v => {
    const o = v.options || {};
    stokM.qty[stokKey(o.Renk, o.Beden)] = Number(v.stock) || 0;
  });
  stokM.open = true;
}
function applyStokMatrix() {
  variants.value.forEach(v => {
    const o = v.options || {};
    const val = Number(stokM.qty[stokKey(o.Renk, o.Beden)]);
    if (!Number.isNaN(val)) v.stock = Math.max(0, Math.floor(val));
  });
  stokM.open = false;
}
const stokMatrixTotal = computed(() =>
  Object.values(stokM.qty).reduce((a, b) => a + (Number(b) || 0), 0)
);

onMounted(() => {
  loadExisting();
  loadCollections();
  loadChannelStatus();
  loadColorDict();
});
</script>

<template>
  <div
    class="flex flex-col h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Sticky header -->
    <div
      class="flex items-center justify-between px-6 py-3 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <div class="flex items-center gap-3 min-w-0">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
          @click="cancel"
        >
          <span class="i-lucide-arrow-left size-3.5" />
          Vazgeç
        </button>
        <h1
          class="text-base font-semibold text-slate-900 dark:text-slate-100 truncate"
        >
          {{ isEdit ? 'Ürünü Düzenle' : 'Yeni Ürün' }}
        </h1>
      </div>
      <div class="flex items-center gap-2">
        <span
          v-if="ticimaxId"
          class="px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700"
        >
          🛒 Ticimax #{{ ticimaxId }}
        </span>
        <button
          v-if="isEdit"
          class="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-slate-800 text-white hover:bg-slate-700"
          @click="openTicimax"
        >
          🛒 Ticimax'e Gönder
        </button>
        <button
          v-if="isEdit"
          class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50"
          :disabled="deleting"
          @click="deleteProduct"
        >
          <span class="i-lucide-trash-2 size-4" />
          Sil
        </button>
        <button
          class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
          :disabled="saving"
          @click="save"
        >
          <span
            :class="
              saving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-check'
            "
            class="size-4"
          />
          {{ saving ? 'Kaydediliyor…' : 'Kaydet' }}
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div class="max-w-6xl mx-auto px-6 py-6">
        <div
          v-if="error"
          class="mb-4 flex items-center gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm"
        >
          <span class="i-lucide-alert-circle size-4 shrink-0" />{{ error }}
        </div>

        <div
          v-if="okMsg"
          class="mb-4 flex items-start gap-2 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 text-sm"
        >
          <span class="i-lucide-check-circle size-4 shrink-0 mt-0.5" />
          <span class="flex-1">{{ okMsg }}</span>
          <button class="text-emerald-500 hover:text-emerald-700" @click="okMsg = ''">
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- ═══ Sol kolon ═══ -->
          <div class="lg:col-span-2 space-y-6">
            <!-- Temel -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Ürün Başlığı
              </label>
              <div class="flex gap-2">
                <input
                  v-model="title"
                  type="text"
                  placeholder="Örn. Kadın Büyük Beden Kısa Kollu Bluz"
                  class="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
                  @blur="onTitleBlur"
                />
                <button
                  v-if="isEdit"
                  class="shrink-0 px-3 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                  :disabled="aiWriting"
                  title="Fotoğraflara bakarak marka dilinde ad + açıklama üretir"
                  @click="generateCopy"
                >
                  {{ aiWriting ? '✨ Yazıyor…' : '✨ İsim & Açıklama Üret' }}
                </button>
              </div>

              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mt-4 mb-1"
              >
                Ürün Kodu · Ana Kod (Parent Code)
              </label>
              <input
                v-model="parentCode"
                type="text"
                placeholder="Örn. ADR-8280 / 8280-26 — ürün ailesini tanımlar"
                class="w-full px-3 py-2 text-sm font-mono border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
              <p class="text-xs text-slate-400 mt-1">
                SKU ve Barkod (GTIN/EAN) varyant seviyesinde — aşağıdaki
                Varyantlar tablosunda her satırda.
              </p>

              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mt-4 mb-1"
              >
                Açıklama
              </label>
              <RichTextEditor
                v-model="description"
                placeholder="Ürün açıklaması, kumaş, materyal, kombin önerileri…"
              />
            </section>

            <!-- Medya (galeri) -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <div class="flex items-center justify-between mb-3">
                <h2
                  class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                >
                  Medya
                </h2>
                <span
                  v-if="uploadingGallery"
                  class="text-xs text-slate-400 flex items-center gap-1"
                >
                  <span class="i-lucide-loader-2 size-3.5 animate-spin" />
                  yükleniyor…
                </span>
              </div>

              <!-- Boş: büyük dropzone -->
              <label
                v-if="!media.images.length"
                class="block border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-10 text-center hover:border-woot-400 transition cursor-pointer"
                @dragover.prevent
                @drop.prevent="onGalleryDrop"
              >
                <span
                  class="i-lucide-image-plus size-8 text-slate-300 mx-auto block mb-2"
                />
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  Görselleri sürükle veya
                  <span class="text-woot-600 font-medium underline">seç</span>
                </p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  class="hidden"
                  @change="onGalleryPick"
                />
              </label>

              <!-- Dolu: kapak + ızgara -->
              <div
                v-else
                class="grid grid-cols-4 gap-3"
                @dragover.prevent
                @drop.prevent="onGalleryDrop"
              >
                <div
                  class="col-span-2 row-span-2 relative group aspect-square rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
                >
                  <img
                    :src="media.images[0]"
                    draggable="true"
                    class="w-full h-full object-cover"
                    @dragstart="draggingUrl = media.images[0]"
                  />
                  <span
                    class="absolute bottom-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium"
                  >
                    Kapak
                  </span>
                  <button
                    class="absolute top-2 right-2 size-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    @click="removeImage(media.images[0])"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </div>

                <div
                  v-for="img in thumbImages"
                  :key="img"
                  class="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700"
                >
                  <img
                    :src="img"
                    draggable="true"
                    class="w-full h-full object-cover"
                    @dragstart="draggingUrl = img"
                  />
                  <div
                    class="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 bg-black/30 transition"
                  >
                    <button
                      class="size-6 rounded-full bg-white/90 text-slate-700 flex items-center justify-center"
                      title="Kapak yap"
                      @click="makeCover(img)"
                    >
                      <span class="i-lucide-star size-3.5" />
                    </button>
                    <button
                      class="size-6 rounded-full bg-white/90 text-red-600 flex items-center justify-center"
                      title="Kaldır"
                      @click="removeImage(img)"
                    >
                      <span class="i-lucide-x size-3.5" />
                    </button>
                  </div>
                </div>

                <button
                  v-if="hiddenImageCount > 0 && !showAllImages"
                  class="aspect-square rounded-lg bg-slate-800/80 text-white text-sm font-semibold flex items-center justify-center hover:bg-slate-800"
                  @click="showAllImages = true"
                >
                  +{{ hiddenImageCount }}
                </button>

                <label
                  class="aspect-square rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 hover:border-woot-400 hover:text-woot-500 cursor-pointer transition"
                >
                  <span class="i-lucide-plus size-6" />
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    class="hidden"
                    @change="onGalleryPick"
                  />
                </label>
              </div>

              <!-- 🎨 RENK STÜDYOSU: AI toplu görselleri renk galerilerine ayrıştırır;
                   yanlışlar sürükle-bırak ile düzeltilir (varyanta özel galeri). -->
              <!-- Kanaldan çekilen ürünlerde ürün galerisi boştur; stüdyo eskiden
                   yalnızca galeri doluyken görünürdü ve o ürünlerde renk bazında
                   görsel yüklemenin yolu kalmıyordu. Artık renk varsa görünür. -->
              <div
                v-if="productColors.length"
                class="mt-5 border-t border-slate-100 dark:border-slate-800 pt-4"
              >
                <div class="flex items-center justify-between mb-2">
                  <h3
                    class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                  >
                    🎨 Renk Stüdyosu
                    <span class="font-normal text-xs text-slate-400">
                      — her rengin kendi galerisi (Ticimax varyant görselleri)
                    </span>
                  </h3>
                  <button
                    class="px-3 py-1.5 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                    :disabled="aiClassifying || !unassignedImages.length"
                    @click="classifyGallery"
                  >
                    {{
                      aiClassifying
                        ? `🎨 Ayrıştırıyor… ${classifyProgress}`
                        : unassignedImages.length
                          ? `🎨 AI Renk Ayrıştır (${unassignedImages.length} görsel)`
                          : '✓ Tüm görseller ayrıştı'
                    }}
                  </button>
                </div>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div
                    v-for="g in studioGroups"
                    :key="g.color"
                    class="rounded-xl border-2 p-2 min-h-[90px] transition"
                    :class="
                      g.color === 'belirsiz'
                        ? 'border-dashed border-yellow-400 bg-yellow-50 dark:bg-slate-800'
                        : 'border-slate-200 dark:border-slate-700'
                    "
                    @dragover.prevent
                    @drop.prevent="onStudioDrop(g.color)"
                  >
                    <div
                      class="text-xs font-bold mb-1.5"
                      :class="
                        g.color === 'belirsiz'
                          ? 'text-yellow-700'
                          : 'text-slate-700 dark:text-slate-300'
                      "
                    >
                      {{
                        g.color === 'belirsiz' ? '❓ Emin olamadım' : g.color
                      }}
                      <span class="font-normal text-slate-400">
                        ({{ g.images.length }})
                      </span>
                    </div>
                    <div class="flex flex-wrap gap-1.5">
                      <img
                        v-for="img in g.images"
                        :key="img"
                        :src="img"
                        draggable="true"
                        class="w-12 h-16 object-cover rounded-md border border-slate-200 dark:border-slate-600 cursor-grab active:cursor-grabbing"
                        title="Sürükleyip başka renge taşıyabilirsin"
                        @dragstart="draggingUrl = img"
                      />
                      <!-- Doğrudan yükleme: galeriye önce toplu yükleyip sonra
                           sürüklemek yerine, rengin fotoğrafları buraya konur. -->
                      <label
                        v-if="g.color !== 'belirsiz'"
                        class="w-12 h-16 rounded-md border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center cursor-pointer hover:border-violet-500 text-slate-400 hover:text-violet-500"
                        :title="`${g.color} için görsel yükle`"
                      >
                        <span
                          v-if="renkYukleniyor === g.color"
                          class="i-lucide-loader-2 size-4 animate-spin"
                        />
                        <span v-else class="i-lucide-image-plus size-4" />
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          class="hidden"
                          @change="studyoGorselYukle($event, g.color)"
                        />
                      </label>
                      <span
                        v-if="!g.images.length"
                        class="text-[10px] text-slate-400 italic self-center"
                      >
                        yükle ya da sürükle
                      </span>
                    </div>
                  </div>
                </div>
                <p
                  v-if="unassignedImages.length && !aiClassifying"
                  class="text-xs text-slate-400 mt-2"
                >
                  {{ unassignedImages.length }} görsel henüz renk galerisine
                  atanmadı — AI butonuna bas ya da elle sürükle.
                </p>
              </div>

              <!-- Video -->
              <h3
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-5 mb-2"
              >
                Video
              </h3>
              <div class="flex gap-2">
                <input
                  v-model="videoInput"
                  type="text"
                  placeholder="Video URL (YouTube/Vimeo/link)"
                  class="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
                  @keydown.enter.prevent="addVideoUrl"
                />
                <button
                  class="px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
                  @click="addVideoUrl"
                >
                  Ekle
                </button>
                <label
                  class="px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 cursor-pointer"
                >
                  Yükle
                  <input
                    type="file"
                    accept="video/*"
                    class="hidden"
                    @change="onVideoPick"
                  />
                </label>
              </div>
              <ul v-if="media.videos.length" class="mt-2 space-y-1">
                <li
                  v-for="v in media.videos"
                  :key="v"
                  class="flex items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-1.5"
                >
                  <span class="truncate flex items-center gap-1.5">
                    <span class="i-lucide-video size-3.5 shrink-0" />{{ v }}
                  </span>
                  <button class="text-red-500" @click="removeVideo(v)">
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </li>
              </ul>
            </section>

            <!-- B2B Fiyatlandırma (açılır blok) -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
            >
              <button
                class="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                @click="pricingOpen = !pricingOpen"
              >
                <span class="flex items-center gap-2">
                  <span
                    class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                  >
                    B2B Fiyatlandırma
                  </span>
                  <span
                    v-if="marginPct !== null"
                    class="px-2 py-0.5 rounded-full text-[11px] font-bold"
                    :class="
                      marginPct > 0
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    "
                  >
                    kâr marjı %{{ marginPct }}
                  </span>
                </span>
                <span
                  class="size-4 text-slate-400 transition-transform"
                  :class="
                    pricingOpen
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                />
              </button>

              <div v-if="pricingOpen" class="px-5 pb-5">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <!-- Maliyet: otomatik gelir -->
                  <div>
                    <label
                      class="flex items-center gap-1.5 text-xs text-slate-500 mb-1"
                    >
                      Maliyet ($ · KDV hariç)
                      <span
                        class="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] font-semibold text-slate-500"
                        title="Trendyol katalog paneliyle aynı ortak maliyet kasası (maliyetler.json)"
                      >
                        ortak kasa
                      </span>
                    </label>
                    <div
                      class="flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800"
                    >
                      <span class="text-sm text-slate-400">$</span>
                      <input
                        v-model.number="pricing.costUsd"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="—"
                        class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-0"
                      />
                      <button
                        class="shrink-0 px-2.5 py-1 rounded-md text-[11px] font-bold text-white disabled:opacity-40"
                        :class="
                          costSaved
                            ? 'bg-green-600'
                            : 'bg-woot-600 hover:bg-woot-700'
                        "
                        :disabled="costSaving || !pricing.costUsd"
                        title="Maliyeti ortak kasaya kaydet (kâr motoru + %15 kuralı buradan okur)"
                        @click="saveCostToKasa"
                      >
                        {{
                          costSaving ? '…' : costSaved ? '✓ Kasada' : 'Kaydet'
                        }}
                      </button>
                    </div>
                  </div>

                  <!-- Satış fiyatı + tümünü uygula -->
                  <div>
                    <label class="block text-xs text-slate-500 mb-1">
                      Satış Fiyatı — adet başı (USD)
                    </label>
                    <div class="flex items-center gap-2">
                      <div
                        class="flex-1 flex items-center gap-2 px-3 py-2 border border-green-300 dark:border-green-800 rounded-lg bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-green-500"
                      >
                        <span
                          class="text-sm font-semibold text-green-700 dark:text-green-400"
                        >
                          $
                        </span>
                        <input
                          v-model.number="pricing.wholesaleUsd"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm font-semibold !bg-transparent !border-0 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-0"
                        />
                      </div>
                      <button
                        class="shrink-0 px-3 py-2 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                        :disabled="!pricing.wholesaleUsd"
                        @click="applyPriceToAllVariants"
                      >
                        Tümünü uygula ({{ variants.length }})
                      </button>
                    </div>
                  </div>
                </div>

                <div class="mt-3">
                  <div class="flex items-center justify-between mb-1.5">
                    <label
                      class="text-xs font-semibold text-slate-600 dark:text-slate-300"
                    >
                      Seri Kurucu — bir koliye hangi bedenden kaç adet?
                    </label>
                    <div class="flex gap-1.5">
                      <button
                        class="px-2 py-1 text-[10.5px] font-semibold rounded-md border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                        @click="seriesFillOnes"
                      >
                        Her bedenden 1
                      </button>
                      <button
                        class="px-2 py-1 text-[10.5px] font-semibold rounded-md border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                        @click="seriesClear"
                      >
                        Temizle
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="productSizes.length"
                    class="grid gap-2"
                    style="
                      grid-template-columns: repeat(
                        auto-fill,
                        minmax(92px, 1fr)
                      );
                    "
                  >
                    <div
                      v-for="sz in productSizes"
                      :key="sz"
                      class="rounded-xl border p-2 text-center transition"
                      :class="
                        qtyOf(sz) > 0
                          ? 'border-green-400 bg-green-50/70 dark:bg-green-900/20 dark:border-green-800'
                          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                      "
                    >
                      <div
                        class="text-[13px] font-bold text-slate-800 dark:text-slate-100"
                      >
                        {{ sz }}
                      </div>
                      <div class="text-[10px] text-slate-400 mb-1.5">
                        stok {{ stockBySize[String(sz).toUpperCase()] || 0 }}
                      </div>
                      <div class="flex items-center justify-center gap-1">
                        <button
                          class="grid size-6 place-items-center rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 font-bold hover:bg-slate-200 disabled:opacity-30"
                          :disabled="qtyOf(sz) === 0"
                          @click="bumpQty(sz, -1)"
                        >
                          −
                        </button>
                        <input
                          :value="qtyOf(sz)"
                          type="number"
                          min="0"
                          class="w-9 !m-0 !p-0 !h-auto text-center text-sm font-bold !bg-transparent !border-0 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-0"
                          @input="setQty(sz, $event.target.value)"
                        />
                        <button
                          class="grid size-6 place-items-center rounded-md bg-green-600 text-white font-bold hover:bg-green-700"
                          @click="bumpQty(sz, 1)"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  <input
                    v-else
                    v-model="pricing.series"
                    type="text"
                    placeholder="Beden seçeneği yok — örn: STD (5)"
                    class="w-full px-3 py-2 text-sm font-mono border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                  />

                  <div class="mt-2 flex flex-wrap items-center gap-2">
                    <span
                      class="px-2.5 py-1 rounded-full text-[11px] font-bold"
                      :class="
                        seriesTotalPieces > 0
                          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                      "
                    >
                      Koli: {{ seriesTotalPieces }} parça
                    </span>
                    <span
                      v-if="seriesBoxPrice"
                      class="px-2.5 py-1 rounded-full text-[11px] font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                    >
                      Koli fiyatı: ${{ seriesBoxPrice }} ({{
                        seriesTotalPieces
                      }}
                      × ${{ pricing.wholesaleUsd }})
                    </span>
                    <span v-else class="text-[11px] text-slate-400">
                      Adet fiyatı + koli içeriği girilince koli fiyatı burada
                      canlı görünür.
                    </span>
                  </div>
                </div>

                <p class="mt-2 text-[11px] text-slate-400">
                  Toptan siteye bu fiyat gider; ödeme = karşılanan parça × adet
                  fiyatı. Ticimax fiyatından bağımsızdır. "Tümünü uygula" fiyatı
                  bütün varyantlara yazar; varyant bazında düzeltmeyi aşağıdaki
                  Toplu Fiyatlandırma'dan yapabilirsin.
                </p>

                <div
                  class="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-3"
                >
                  <div>
                    <label class="block text-xs text-slate-400 mb-1">
                      Üstü Çizili Fiyat (ops.)
                    </label>
                    <input
                      v-model.number="pricing.compareAt"
                      type="number"
                      step="0.01"
                      class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-slate-400 mb-1">KDV %</label>
                    <input
                      v-model.number="pricing.taxRate"
                      type="number"
                      step="1"
                      class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- Varyantlar -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <div class="flex items-center justify-between mb-3">
                <h2
                  class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                >
                  Varyantlar
                </h2>
                <div class="flex items-center gap-2">
                  <label
                    v-if="isEdit && parentCode"
                    class="text-xs font-semibold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 cursor-pointer"
                    title="Adrom'dan yeniden aldığınız Excel'i yükleyin; sadece eksik renkler eklenir"
                  >
                    📥 Excel'den Renk Ekle
                    <input
                      type="file"
                      accept=".xlsx"
                      class="hidden"
                      @change="renkEkleDosyaSecildi"
                    />
                  </label>
                  <button
                    v-if="productColors.length && productSizes.length"
                    class="text-xs font-semibold px-3 py-1.5 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600"
                    @click="openStokMatrix"
                  >
                    ⚡ Hızlı Stok Girişi
                  </button>
                  <button
                    v-if="options.length < 3"
                    class="text-xs text-woot-600 font-medium flex items-center gap-1"
                    @click="addOption"
                  >
                    <span class="i-lucide-plus size-3.5" /> Seçenek ekle (Renk,
                    Beden…)
                  </button>
                </div>
              </div>

              <div
                v-for="(opt, oIdx) in options"
                :key="oIdx"
                class="rounded-xl bg-slate-50 dark:bg-slate-800 p-3 mb-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <input
                    v-model="opt.name"
                    type="text"
                    placeholder="Seçenek adı (Renk / Beden)"
                    class="flex-1 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                  />
                  <button class="text-red-500 p-1" @click="removeOption(oIdx)">
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
                <div class="flex flex-wrap gap-1.5 mb-2">
                  <span
                    v-for="val in opt.values"
                    :key="val"
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 text-xs font-medium"
                  >
                    {{ val }}
                    <button @click="removeOptionValue(opt, val)">
                      <span class="i-lucide-x size-3" />
                    </button>
                  </span>
                </div>
                <input
                  v-model="opt.valueInput"
                  type="text"
                  :list="opt.name === 'Renk' ? 'renk-sozlugu' : undefined"
                  :placeholder="
                    opt.name === 'Renk'
                      ? 'Renk Sözlüğü\'nden seç ya da yaz, Enter\'a bas'
                      : 'Değer yaz, Enter\'a bas (S, M, XL…)'
                  "
                  class="w-full px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                  @keydown.enter.prevent="addOptionValue(opt)"
                />
                <datalist id="renk-sozlugu">
                  <option
                    v-for="c in colorDict"
                    :key="c.name"
                    :value="c.name"
                  />
                </datalist>
              </div>

              <!-- Toplu Fiyatlandırma: filtrele -> uygula -->
              <div
                v-if="variants.length"
                class="mt-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3"
              >
                <div
                  class="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2"
                >
                  <span
                    class="i-lucide-badge-dollar-sign size-4 text-green-600"
                  />
                  Toplu Fiyatlandırma
                  <span class="font-normal text-slate-400">
                    · hedef: {{ bulkTargets.length }} /
                    {{ variants.length }} varyant
                  </span>
                </div>
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  <select
                    v-for="opt in bulkFilterOptions"
                    :key="opt.name"
                    v-model="bulk.filters[opt.name]"
                    class="w-40 shrink-0 px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
                  >
                    <option value="">{{ opt.name }}: tümü</option>
                    <option v-for="val in opt.values" :key="val" :value="val">
                      {{ val }}
                    </option>
                  </select>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <div
                    class="w-28 shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  >
                    <span class="text-xs text-slate-400">$</span>
                    <input
                      v-model.number="bulk.price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Fiyat"
                      class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-xs !bg-transparent !border-0 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-0"
                    />
                  </div>
                  <button
                    class="shrink-0 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-40"
                    :disabled="!bulk.price || !bulkTargets.length"
                    @click="applyBulkPrice"
                  >
                    Uygula ({{ bulkTargets.length }})
                  </button>

                  <span class="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />

                  <div
                    class="w-28 shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  >
                    <span class="text-xs text-slate-400">%</span>
                    <input
                      v-model.number="bulk.pct"
                      type="number"
                      step="1"
                      placeholder="+10 / -5"
                      class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-xs !bg-transparent !border-0 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-0"
                    />
                  </div>
                  <button
                    class="shrink-0 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-woot-500 text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 disabled:opacity-40"
                    :disabled="!bulk.pct || !bulkTargets.length"
                    @click="applyBulkPct"
                  >
                    % Zam / İndirim
                  </button>
                </div>
                <p class="mt-1.5 text-[11px] text-slate-400">
                  Filtre seçmeden uygularsan tüm varyantlara yazılır. % işlemi
                  yalnızca fiyatı dolu varyantları günceller.
                </p>
              </div>

              <!-- Gruplu akordiyon (2+ seçenek: renge göre) -->
              <div
                v-if="variants.length && isGrouped"
                class="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 mt-2 overflow-hidden"
              >
                <div v-for="group in variantGroups" :key="group.key">
                  <button
                    class="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    @click="toggleGroup(group.key)"
                  >
                    <div
                      class="size-9 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0 flex items-center justify-center"
                    >
                      <img
                        v-if="groupImage(group)"
                        :src="groupImage(group)"
                        class="w-full h-full object-cover"
                      />
                      <span
                        v-else
                        class="i-lucide-image size-4 text-slate-300"
                      />
                    </div>
                    <div class="min-w-0">
                      <p
                        class="text-sm font-medium text-slate-800 dark:text-slate-100"
                      >
                        {{ group.label }}
                      </p>
                      <p class="text-xs text-slate-400">
                        {{ group.variants.length }} varyasyon
                      </p>
                    </div>
                    <span class="ml-auto text-xs text-slate-500 tabular-nums">
                      stok {{ groupStock(group) }}
                    </span>
                    <span
                      :class="
                        expandedGroups.has(group.key)
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="size-4 text-slate-400 shrink-0"
                    />
                  </button>
                  <div
                    v-if="expandedGroups.has(group.key)"
                    class="bg-slate-50/60 dark:bg-slate-800/30"
                  >
                    <div
                      v-for="v in group.variants"
                      :key="v.key"
                      class="pl-6 pr-3 py-3 border-t border-slate-100 dark:border-slate-800"
                    >
                      <div class="flex items-center gap-3 mb-2">
                        <button
                          class="size-9 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden hover:border-woot-400 shrink-0"
                          @click="openPicker(v)"
                        >
                          <img
                            v-if="v.image"
                            :src="v.image"
                            class="w-full h-full object-cover"
                          />
                          <span
                            v-else
                            class="i-lucide-image-plus size-3.5 text-slate-300"
                          />
                        </button>
                        <span
                          class="text-sm font-semibold text-slate-700 dark:text-slate-200"
                        >
                          {{ subLabel(v) }}
                        </span>
                      </div>
                      <div class="grid grid-cols-2 lg:grid-cols-6 gap-2 pl-12">
                        <label class="col-span-2 flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            SKU
                          </span>
                          <input
                            v-model="v.sku"
                            type="text"
                            placeholder="Stok kodu"
                            class="w-full px-2 py-1 text-xs font-mono border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                          />
                        </label>
                        <label class="flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            Barkod
                          </span>
                          <input
                            v-model="v.barcode"
                            type="text"
                            placeholder="GTIN/EAN"
                            class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          />
                        </label>
                        <label class="flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            Fiyat $
                          </span>
                          <input
                            v-model.number="v.price"
                            type="number"
                            step="0.01"
                            class="w-full px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          />
                        </label>
                        <label class="flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            Maliyet $
                          </span>
                          <input
                            v-model.number="v.costUsd"
                            type="number"
                            step="0.01"
                            class="w-full px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          />
                        </label>
                        <label class="flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            Stok
                          </span>
                          <input
                            v-model.number="v.stock"
                            type="number"
                            class="w-full px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Düz tablo (0-1 seçenek) -->
              <div
                v-else-if="variants.length"
                class="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto mt-2"
              >
                <table class="w-full text-sm">
                  <thead
                    class="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
                  >
                    <tr>
                      <th class="text-left font-medium px-2 py-2">Görsel</th>
                      <th class="text-left font-medium px-2 py-2">Varyant</th>
                      <th class="text-left font-medium px-2 py-2">SKU</th>
                      <th class="text-left font-medium px-2 py-2">Barkod</th>
                      <th class="text-right font-medium px-2 py-2">Fiyat $</th>
                      <th class="text-right font-medium px-2 py-2">
                        Maliyet $
                      </th>
                      <th class="text-right font-medium px-2 py-2">Stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="v in variants"
                      :key="v.key"
                      class="border-t border-slate-100 dark:border-slate-800"
                    >
                      <td class="px-2 py-1.5">
                        <button
                          class="size-9 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden hover:border-woot-400"
                          @click="openPicker(v)"
                        >
                          <img
                            v-if="v.image"
                            :src="v.image"
                            class="w-full h-full object-cover"
                          />
                          <span
                            v-else
                            class="i-lucide-image-plus size-4 text-slate-300"
                          />
                        </button>
                      </td>
                      <td
                        class="px-2 py-1.5 font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap"
                      >
                        {{ v.key }}
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model="v.sku"
                          type="text"
                          class="w-28 px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model="v.barcode"
                          type="text"
                          class="w-28 px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model.number="v.price"
                          type="number"
                          step="0.01"
                          class="w-20 px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model.number="v.costUsd"
                          type="number"
                          step="0.01"
                          class="w-20 px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model.number="v.stock"
                          type="number"
                          class="w-16 px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="text-xs text-slate-400 mt-1">
                Seçenek eklemezsen ürün tek varyant olarak kaydedilir.
              </p>
            </section>

            <!-- SEO -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Arama Motoru (SEO)
              </h2>
              <label class="block text-xs text-slate-500 mb-1">
                Sayfa Başlığı
              </label>
              <input
                v-model="seo.title"
                type="text"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />
              <label class="block text-xs text-slate-500 mb-1">
                Meta Açıklama
              </label>
              <textarea
                v-model="seo.description"
                rows="2"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />
              <label class="block text-xs text-slate-500 mb-1">
                URL Uzantısı (handle)
              </label>
              <input
                v-model="seo.handle"
                type="text"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
            </section>
          </div>

          <!-- ═══ Sağ kolon ═══ -->
          <div class="space-y-6">
            <!-- 🛒 Ticimax (perakende) fiyatı — SKU Kâr Motoru bağlantılı -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <div class="flex items-center justify-between mb-3">
                <h2
                  class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                >
                  🛒 Ticimax Fiyatı (TL)
                </h2>
                <button
                  class="text-xs font-semibold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                  :disabled="priceCalc.busy"
                  title="SKU Kâr Motoru vitrin bandından hesaplar (maliyet $ üzerinden)"
                  @click="calcTicimaxPrice"
                >
                  {{ priceCalc.busy ? 'Hesaplanıyor…' : '🧮 Fiyat Hesapla' }}
                </button>
              </div>
              <label class="block text-xs text-slate-500 mb-1">
                Satış fiyatı (vitrin, KDV dahil)
              </label>
              <input
                v-model.number="pricing.ticimaxPrice"
                type="number"
                min="0"
                step="0.01"
                placeholder="örn. 899.90"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
              <label class="block text-xs text-slate-500 mt-3 mb-1">
                Piyasa fiyatı (üstü çizili — boş bırakılabilir)
              </label>
              <input
                v-model.number="pricing.ticimaxCompareAt"
                type="number"
                min="0"
                step="0.01"
                placeholder="örn. 1099.90"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
              <p
                v-if="priceCalc.info"
                class="text-xs mt-2"
                :class="priceCalc.quotes ? 'text-green-700' : 'text-yellow-700'"
              >
                {{ priceCalc.info }}
              </p>
              <div
                v-if="priceCalc.quotes && priceCalc.quotes.site"
                class="text-xs text-slate-500 mt-1"
              >
                Bant: min {{ priceCalc.quotes.site.min_price }} · sürdürülebilir
                {{ priceCalc.quotes.site.sustainable_price }} · önerilen
                {{ priceCalc.quotes.site.recommended_price }}
              </div>
            </section>

            <!-- 🏬 ModaXLarge (Shopify perakende) fiyatı — boşsa Koza fiyatına düşer -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1"
              >
                🏬 ModaXLarge Fiyatı (TL)
              </h2>
              <p class="text-xs text-slate-500 mb-3">
                Boş bırakırsan Koza fiyatı kullanılır. Yalnızca ModaXLarge'da
                farklı satmak istiyorsan doldur.
              </p>
              <label class="block text-xs text-slate-500 mb-1">
                Satış fiyatı (vitrin, KDV dahil)
              </label>
              <input
                v-model.number="pricing.modaxPrice"
                type="number"
                min="0"
                step="0.01"
                :placeholder="
                  pricing.ticimaxPrice
                    ? `Koza fiyatı: ${pricing.ticimaxPrice}`
                    : 'örn. 899.90'
                "
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
              <label class="block text-xs text-slate-500 mt-3 mb-1">
                Piyasa fiyatı (üstü çizili)
              </label>
              <input
                v-model.number="pricing.modaxCompareAt"
                type="number"
                min="0"
                step="0.01"
                :placeholder="
                  pricing.ticimaxCompareAt
                    ? `Koza: ${pricing.ticimaxCompareAt}`
                    : 'boş bırakılabilir'
                "
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
              <p class="text-xs text-slate-500 mt-2">
                Şu an gönderilecek:
                <b class="text-slate-700 dark:text-slate-300">
                  {{ modaxEffectivePrice || '—' }} TL
                </b>
                <template v-if="modaxEffectiveCompareAt">
                  · üstü çizili {{ modaxEffectiveCompareAt }} TL
                </template>
              </p>
            </section>

            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Durum
              </h2>
              <select
                v-model="status"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              >
                <option value="draft">Taslak</option>
                <option value="active">Aktif</option>
              </select>
            </section>

            <!-- Satış Kanalları (bağ durumu — Entegrasyon Merkezi) -->
            <section
              v-if="isEdit"
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                <span class="i-lucide-plug size-4 text-slate-400" />
                Satış Kanalları
              </h2>

              <div
                v-if="channelsLoading"
                class="flex items-center gap-2 py-3 text-slate-400 text-xs"
              >
                <span class="i-lucide-loader-2 size-4 animate-spin" /> Kanal
                durumu yükleniyor…
              </div>

              <template v-else>
                <div
                  v-for="ch in channelCards.filter(c =>
                    c.channelType.startsWith('shopify')
                  )"
                  :key="ch.channelId"
                  class="flex flex-col gap-2.5"
                >
                  <div class="flex items-center gap-2.5">
                    <div
                      class="grid size-8 shrink-0 place-items-center rounded-lg text-white text-sm font-bold"
                      style="background: #5e8e3e"
                    >
                      S
                    </div>
                    <b
                      class="text-[13px] text-slate-800 dark:text-slate-100 min-w-0 truncate"
                      >{{ ch.name }}</b>
                    <span
                      v-if="ch.link"
                      class="ml-auto shrink-0 rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-[10.5px] font-bold text-green-700 dark:text-green-300"
                    >
                      ● Bağlı
                    </span>
                    <span
                      v-else
                      class="ml-auto shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10.5px] font-bold text-slate-400"
                    >
                      Bağlı değil
                    </span>
                  </div>

                  <!-- KOLEKSİYONLAR: akıllı öneri seçili gelir, ✕ ile çıkar,
                       aramayla ekle, Güncelle ile uygula (tek birleşik liste) -->
                  <div
                    v-if="collectionStateFor(ch.channelId).list.length"
                    class="flex flex-col gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"
                  >
                    <div class="flex items-center gap-2">
                      <span
                        class="text-[10px] font-bold uppercase tracking-wide text-slate-400"
                        >Koleksiyonlar</span>
                      <span
                        v-if="
                          collectionStateFor(ch.channelId).source === 'auto' &&
                          collectionStateFor(ch.channelId).selected.length
                        "
                        class="rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 px-1.5 py-0.5 text-[9px] font-bold"
                        >⚡ akıllı öneri — düzenleyebilirsin</span>
                    </div>
                    <div
                      v-if="collectionStateFor(ch.channelId).selected.length"
                      class="flex flex-wrap gap-1"
                    >
                      <span
                        v-for="cid in collectionStateFor(ch.channelId).selected"
                        :key="cid"
                        class="inline-flex items-center gap-1 rounded-full bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 px-2 py-0.5 text-[10.5px] font-semibold"
                      >
                        <span
                          v-if="
                            collectionStateFor(ch.channelId).suggested.includes(
                              cid
                            )
                          "
                          >⚡</span
                        >
                        {{ collectionTitle(ch.channelId, cid) }}
                        <span
                          v-if="!collectionSelectable(ch.channelId, cid)"
                          title="Akıllı koleksiyon — kural devrine kadar üyelik başlık kuralıyla belirlenir; seçimin kaydedilir ve devirde uygulanır."
                          >🔒</span>
                        <button
                          class="hover:text-red-600 font-bold"
                          @click="removeCollection(ch.channelId, cid)"
                        >
                          ✕
                        </button>
                      </span>
                    </div>
                    <div
                      v-else
                      class="rounded bg-red-50 dark:bg-red-900/20 px-2 py-1.5 text-[11px] text-red-700 dark:text-red-300"
                    >
                      ⚠ Hiç koleksiyon seçili değil — ürün vitrinde hiçbir
                      koleksiyonda görünmez. Aramayla ekleyin ya da başlığa tür
                      kelimesi (Bluz, Elbise…) yazın.
                    </div>
                    <input
                      v-model="collectionStateFor(ch.channelId).search"
                      type="text"
                      placeholder="🔍 Koleksiyon ara ve ekle…"
                      class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-[11.5px] text-slate-700 dark:text-slate-200"
                    />
                    <div
                      v-if="collectionSearchResults(ch.channelId).length"
                      class="flex flex-col rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden"
                    >
                      <button
                        v-for="c in collectionSearchResults(ch.channelId)"
                        :key="c.id"
                        class="px-2.5 py-1.5 text-left text-[11.5px] text-slate-600 dark:text-slate-300 hover:bg-woot-50 dark:hover:bg-woot-900/20"
                        @click="addCollection(ch.channelId, c.id)"
                      >
                        ＋ {{ c.title }}
                        <span class="text-slate-400">({{ c.productsCount }} ürün)</span>
                        <span
                          v-if="!c.selectable"
                          class="text-slate-400"
                          title="Kural devrine kadar başlık kuralıyla çalışır"
                          >🔒</span>
                      </button>
                    </div>
                    <div class="flex items-center gap-2 pt-0.5">
                      <button
                        class="rounded-lg bg-woot-600 hover:bg-woot-700 disabled:opacity-40 text-white px-3 py-1.5 text-[11px] font-bold"
                        :disabled="
                          collectionStateFor(ch.channelId).applying ||
                          !collectionsDirty(ch.channelId)
                        "
                        @click="applyCollections(ch)"
                      >
                        {{
                          collectionStateFor(ch.channelId).applying
                            ? 'Uygulanıyor…'
                            : 'Koleksiyonları Güncelle'
                        }}
                      </button>
                      <span
                        v-if="
                          collectionsDirty(ch.channelId) &&
                          !collectionStateFor(ch.channelId).applying
                        "
                        class="text-[10.5px] text-yellow-600 dark:text-yellow-400 font-semibold"
                        >kaydedilmemiş değişiklik</span>
                    </div>
                    <div
                      v-if="collectionStateFor(ch.channelId).result"
                      class="text-[11px]"
                      :class="
                        collectionStateFor(ch.channelId).result.startsWith('⚠')
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-green-700 dark:text-green-400'
                      "
                    >
                      {{ collectionStateFor(ch.channelId).result }}
                    </div>
                  </div>

                  <!-- Bağlıysa: bağ özeti -->
                  <template v-if="ch.link">
                    <div class="text-[11px] text-slate-400 leading-relaxed">
                      {{ ch.link.variantCount }} kanal varyantı ·
                      {{ ch.link.colorsMatched }}/{{ ch.link.variantCount }}
                      renk eşli ·
                      <a
                        :href="ch.link.externalUrl"
                        target="_blank"
                        class="text-woot-600 font-semibold"
                        >Mağazada gör ↗</a>
                    </div>
                    <div
                      v-if="
                        ch.channelType === 'shopify_adrom' &&
                        ch.link.unmatchedChannelColors.length
                      "
                      class="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900 px-2.5 py-1.5 text-[11px] text-yellow-800 dark:text-yellow-300"
                    >
                      ⚠ Eşleşmeyen renk:
                      {{ ch.link.unmatchedChannelColors.join(', ') }} —
                      <router-link
                        :to="{
                          name: 'integrations_index',
                          params: { accountId },
                        }"
                        class="font-semibold underline"
                      >
                        Eşleme Masası
                      </router-link>
                    </div>
                    <div
                      class="flex flex-wrap gap-1.5 text-[10px] font-semibold"
                    >
                      <span
                        class="rounded bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5"
                        >STOK otomatik*</span>
                      <span
                        class="rounded bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5"
                        >FİYAT otomatik*</span>
                      <span
                        class="rounded bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5"
                        >İÇERİK elle</span>
                    </div>
                    <button
                      v-if="ch.link.state !== 'live'"
                      class="w-full px-3 py-2 text-xs font-semibold rounded-lg text-white disabled:opacity-40"
                      :class="
                        activatingBy[ch.channelId]
                          ? 'bg-slate-400'
                          : 'bg-woot-500 hover:bg-woot-600'
                      "
                      :disabled="activatingBy[ch.channelId]"
                      title="Durumu Aktif yapar ve Online Store kanalına açar — müşteri görür"
                      @click="activateOnChannel(ch)"
                    >
                      <span
                        v-if="activatingBy[ch.channelId]"
                        class="i-lucide-loader-2 size-3.5 animate-spin inline-block align-middle mr-1"
                      />
                      🌐 Vitrine Aç (Aktif + Online Store)
                    </button>
                    <div
                      v-else
                      class="w-full px-3 py-2 text-xs font-semibold rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-center"
                    >
                      🌐 Vitrinde — Online Store'da yayında
                    </div>
                    <p
                      v-if="publishResultBy[ch.channelId]"
                      class="text-[11px] text-green-700 dark:text-green-300"
                    >
                      ✓ {{ publishResultBy[ch.channelId] }}
                    </p>
                  </template>

                  <!-- Bağlı değilse: ön kontrol + yönlendirme -->
                  <template v-else>
                    <div class="flex flex-col gap-1">
                      <span
                        class="text-[10px] font-bold uppercase tracking-wide text-slate-400"
                        >Yayın ön kontrolü</span>
                      <span
                        class="flex items-center gap-1.5 text-[11.5px]"
                        :class="
                          ch.readiness.price ? 'text-slate-500' : 'text-red-600'
                        "
                      >
                        <span
                          :class="
                            ch.readiness.price
                              ? 'i-lucide-check-circle-2 text-green-600'
                              : 'i-lucide-x-circle'
                          "
                          class="size-3.5"
                        />
                        {{
                          ch.channelType === 'shopify_adrom'
                            ? 'Toptan fiyat'
                            : 'Satış fiyatı'
                        }}
                        {{ ch.readiness.price ? 'girildi' : 'girilmedi' }}
                      </span>
                      <span
                        class="flex items-center gap-1.5 text-[11.5px]"
                        :class="
                          ch.readiness.image ? 'text-slate-500' : 'text-red-600'
                        "
                      >
                        <span
                          :class="
                            ch.readiness.image
                              ? 'i-lucide-check-circle-2 text-green-600'
                              : 'i-lucide-x-circle'
                          "
                          class="size-3.5"
                        />
                        Görsel {{ ch.readiness.image ? 'var' : 'yok' }}
                      </span>
                      <span
                        v-if="ch.channelType === 'shopify_adrom'"
                        class="flex items-center gap-1.5 text-[11.5px]"
                        :class="
                          ch.readiness.series
                            ? 'text-slate-500'
                            : 'text-red-600'
                        "
                      >
                        <span
                          :class="
                            ch.readiness.series
                              ? 'i-lucide-check-circle-2 text-green-600'
                              : 'i-lucide-x-circle'
                          "
                          class="size-3.5"
                        />
                        Seri içeriği
                        {{ ch.readiness.series ? 'tanımlı' : 'tanımsız' }}
                      </span>
                      <span
                        class="flex items-center gap-1.5 text-[11.5px]"
                        :class="
                          ch.readiness.variants
                            ? 'text-slate-500'
                            : 'text-red-600'
                        "
                      >
                        <span
                          :class="
                            ch.readiness.variants
                              ? 'i-lucide-check-circle-2 text-green-600'
                              : 'i-lucide-x-circle'
                          "
                          class="size-3.5"
                        />
                        Varyantlar
                        {{ ch.readiness.variants ? 'tanımlı' : 'yok' }}
                      </span>
                    </div>
                    <!-- Ürün kanalda ZATEN varsa (bağ yok ama SKU eşleşti):
                         yayınlamak kopya açardı — düğme yerine uyarı. -->
                    <div
                      v-if="ch.existing"
                      class="rounded-lg border border-yellow-200 dark:border-yellow-900 bg-yellow-50 dark:bg-yellow-900/20 px-2.5 py-2 text-[11.5px] text-yellow-800 dark:text-yellow-300 flex flex-col gap-1"
                    >
                      <b>
                        Bu ürün kanalda zaten var —
                        {{
                          ch.existing.published ? 'vitrinde yayında' : 'taslak'
                        }}
                      </b>
                      <span>
                        Yeniden yayınlamak kopya ürün açar, o yüzden kapalı.
                      </span>
                      <a
                        v-if="ch.existing.url"
                        :href="ch.existing.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="font-semibold underline"
                      >
                        Kanaldaki ürünü aç →
                      </a>
                    </div>
                    <template v-else>
                      <button
                        class="w-full px-3 py-2 text-xs font-semibold rounded-lg text-white disabled:opacity-40"
                        :class="
                          publishingBy[ch.channelId]
                            ? 'bg-slate-400'
                            : 'bg-green-600 hover:bg-green-700'
                        "
                        :disabled="
                          publishingBy[ch.channelId] ||
                          !ch.readiness.price ||
                          !ch.readiness.series ||
                          !ch.readiness.image
                        "
                        :title="
                          publishActiveBy[ch.channelId]
                            ? 'Direkt AKTİF yayınlar — anında vitrine düşer'
                            : 'TASLAK oluşturur — incele, sonra Vitrine Aç'
                        "
                        @click="publishToChannel(ch)"
                      >
                        <span
                          v-if="publishingBy[ch.channelId]"
                          class="i-lucide-loader-2 size-3.5 animate-spin inline-block align-middle mr-1"
                        />
                        🚀 {{ ch.name.replace('Shopify · ', '') }}'a Yayınla
                        {{
                          publishActiveBy[ch.channelId]
                            ? '(AKTİF — direkt vitrine)'
                            : '(taslak)'
                        }}
                      </button>
                      <label
                        class="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer"
                      >
                        <input
                          v-model="publishActiveBy[ch.channelId]"
                          type="checkbox"
                          class="rounded border-slate-300"
                        />
                        Taslağı atla, hemen aktif yayınla (Online Store dahil)
                      </label>
                    </template>
                    <p
                      v-if="publishResultBy[ch.channelId]"
                      class="text-[11px] text-green-700 dark:text-green-300"
                    >
                      ✓ {{ publishResultBy[ch.channelId] }}
                    </p>
                    <p
                      v-if="
                        !ch.readiness.price ||
                        !ch.readiness.series ||
                        !ch.readiness.image
                      "
                      class="text-[11px] text-slate-400"
                    >
                      Eksikler tamamlanınca buton açılır.
                      {{
                        ch.channelType === 'shopify_adrom'
                          ? 'Fiyat ve seri, B2B Fiyatlandırma bloğunda.'
                          : 'Fiyat, ModaXLarge Fiyatı bloğunda (boşsa Koza fiyatı kullanılır).'
                      }}
                    </p>
                    <!-- Eşleme Masası TOPTANA özgü: kanalda hazır duran ürünü
                         ÜM kaydıyla eşleştirir. Perakendede ürün sıfırdan
                         bizde doğar, eşleştirilecek bir şey yok. -->
                    <router-link
                      v-if="ch.channelType === 'shopify_adrom'"
                      :to="{
                        name: 'integrations_index',
                        params: { accountId },
                      }"
                      class="w-full block text-center px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      🔗 Ya da mevcut ürünle eşle (Eşleme Masası)
                    </router-link>
                  </template>

                </div>

                <p
                  v-if="!channelCards.length"
                  class="text-[11.5px] text-slate-400"
                >
                  Kanal tanımı bulunamadı.
                </p>
              </template>
            </section>

            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Organizasyon
              </h2>

              <label class="block text-xs text-slate-500 mb-1">
                Marka (Vendor)
              </label>
              <input
                v-model="vendor"
                type="text"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />

              <!-- Koleksiyonlar: çok seçimli, gerçekten üye yapar -->
              <label class="block text-xs text-slate-500 mb-1">
                Koleksiyonlar
              </label>
              <div class="relative mb-3">
                <button
                  class="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-left"
                  @click="collectionsOpen = !collectionsOpen"
                >
                  <span v-if="!collectionIds.length" class="text-slate-400">
                    Koleksiyon seç…
                  </span>
                  <span v-else class="flex flex-wrap gap-1">
                    <span
                      v-for="cid in collectionIds"
                      :key="cid"
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 text-xs font-medium"
                    >
                      {{
                        (allCollections.find(c => c.id === cid) || {}).title ||
                        cid
                      }}
                      <span
                        class="i-lucide-x size-3 cursor-pointer opacity-60 hover:opacity-100"
                        @click.stop="toggleCollection(cid)"
                      />
                    </span>
                  </span>
                  <span
                    class="size-4 shrink-0 text-slate-400"
                    :class="
                      collectionsOpen
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                  />
                </button>
                <div
                  v-if="collectionsOpen"
                  class="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg"
                >
                  <p
                    v-if="!manualCollections.length"
                    class="px-3 py-3 text-xs text-slate-400"
                  >
                    Henüz koleksiyon yok. Koleksiyonlar sayfasından oluştur.
                  </p>
                  <label
                    v-for="c in manualCollections"
                    :key="c.id"
                    class="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      :checked="collectionIds.includes(c.id)"
                      class="rounded border-slate-300"
                      @change="toggleCollection(c.id)"
                    />
                    {{ c.title }}
                  </label>
                  <div
                    v-if="smartCollections.length"
                    class="border-t border-slate-100 dark:border-slate-700 px-3 py-2"
                  >
                    <p
                      class="text-[10px] uppercase tracking-wide text-slate-400 mb-1"
                    >
                      Akıllı koleksiyonlar (kurala göre otomatik)
                    </p>
                    <p
                      v-for="c in smartCollections"
                      :key="c.id"
                      class="text-xs text-slate-400 py-0.5"
                    >
                      {{ c.title }}
                    </p>
                  </div>
                </div>
              </div>

              <label class="block text-xs text-slate-500 mb-1">
                Kategori (ürün tipi)
              </label>
              <input
                v-model="productType"
                type="text"
                placeholder="Bluz, Elbise, Takım…"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />

              <label class="block text-xs text-slate-500 mb-1">
                Kol boyu
                <span v-if="sleeve && sleeveSource" class="text-slate-400">
                  · {{ SLEEVE_SOURCE_LABEL[sleeveSource] || sleeveSource }}
                </span>
              </label>
              <select
                v-model="sleeve"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-1"
              >
                <option v-for="o in SLEEVE_OPTIONS" :key="o.value" :value="o.value">
                  {{ o.label }}
                </option>
              </select>
              <p class="text-[11px] text-slate-400 mb-3">
                Uzun kollular vitrinde öne çıkar ve Sonbahar-Kış Koleksiyonu'na girer; kısa/kolsuz İlkbahar-Yaz'a.
              </p>

              <label class="block text-xs text-slate-500 mb-1">
                Google Kategorisi (Shopping)
              </label>
              <input
                v-model="googleCategory"
                type="text"
                list="google-cats"
                placeholder="Apparel & Accessories > Clothing > …"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />
              <datalist id="google-cats">
                <option v-for="c in GOOGLE_CATEGORIES" :key="c" :value="c" />
              </datalist>

              <label class="block text-xs text-slate-500 mb-1">Etiketler</label>
              <div class="flex flex-wrap gap-1.5 mb-2">
                <span
                  v-for="t in tags"
                  :key="t"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs"
                >
                  {{ t }}
                  <button @click="removeTag(t)">
                    <span class="i-lucide-x size-3" />
                  </button>
                </span>
              </div>
              <input
                v-model="tagInput"
                type="text"
                placeholder="Etiket yaz, Enter'a bas"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                @keydown.enter.prevent="addTag"
              />
            </section>

            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                <span class="i-lucide-package size-4 text-slate-400" />
                Kargo &amp; Teslimat
              </h2>

              <!-- Ağırlık + birim -->
              <label class="block text-xs text-slate-500 mb-1">Ağırlık</label>
              <div class="flex items-center gap-2 mb-3">
                <input
                  v-model.number="shipping.weight"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  class="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                />
                <select
                  v-model="shipping.weightUnit"
                  class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                >
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                </select>
              </div>

              <!-- Paket boyutları -> otomatik desi -->
              <label class="block text-xs text-slate-500 mb-1">
                Paket Boyutları (cm)
              </label>
              <div class="grid grid-cols-3 gap-2 mb-2">
                <input
                  v-model.number="shipping.width"
                  type="number"
                  min="0"
                  placeholder="En"
                  class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                />
                <input
                  v-model.number="shipping.length"
                  type="number"
                  min="0"
                  placeholder="Boy"
                  class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                />
                <input
                  v-model.number="shipping.height"
                  type="number"
                  min="0"
                  placeholder="Yükseklik"
                  class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                />
              </div>

              <label
                class="flex items-center justify-between text-xs text-slate-500 mb-1"
              >
                <span class="flex items-center gap-1.5">
                  Desi
                  <span
                    v-if="!shipping.desiManual && autoDesi !== null"
                    class="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-[10px] font-semibold text-green-700 dark:text-green-300"
                  >
                    otomatik: E×B×Y / 3000
                  </span>
                </span>
                <button
                  v-if="shipping.desiManual"
                  class="text-woot-600 text-[11px] font-medium"
                  @click="resetDesiAuto"
                >
                  otomatiğe dön
                </button>
              </label>
              <input
                v-model.number="shipping.desi"
                type="number"
                step="0.01"
                min="0"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
                @input="onDesiInput"
              />

              <!-- Hazırlık süresi -->
              <label class="block text-xs text-slate-500 mb-1">
                Kargoya Hazırlık Süresi (iş günü)
              </label>
              <input
                v-model.number="shipping.prepDays"
                type="number"
                min="0"
                step="1"
                placeholder="örn. 1"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />

              <!-- Gümrük (uluslararası satış için) -->
              <div class="pt-3 border-t border-slate-100 dark:border-slate-800">
                <p
                  class="text-[10px] uppercase tracking-wide text-slate-400 mb-2"
                >
                  Gümrük · uluslararası satış
                </p>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="block text-xs text-slate-500 mb-1">
                      Menşe Ülke
                    </label>
                    <select
                      v-model="shipping.originCountry"
                      class="w-full px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                    >
                      <option value="TR">Türkiye (TR)</option>
                      <option value="CN">Çin (CN)</option>
                      <option value="IN">Hindistan (IN)</option>
                      <option value="BD">Bangladeş (BD)</option>
                      <option value="">Diğer / boş</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs text-slate-500 mb-1">
                      GTİP / HS Kodu
                    </label>
                    <input
                      v-model="shipping.hsCode"
                      type="text"
                      placeholder="örn. 6104.43"
                      class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <!-- Varyant görseli seçici popup -->
    <div
      v-if="picker.open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div class="absolute inset-0 bg-black/50" @click="closePicker" />
      <div
        class="relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 shadow-xl overflow-hidden"
      >
        <div
          class="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800"
        >
          <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Varyant görseli seç
            <span class="ml-1 font-normal text-slate-400">{{ pickerImages.length }} / {{ media.images.length }}</span>
          </h3>
          <button
            class="text-slate-400 hover:text-slate-600"
            @click="closePicker"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <!-- Arama -->
        <div v-if="media.images.length" class="px-5 pt-4">
          <div
            class="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-woot-500"
          >
            <span class="i-lucide-search size-4 shrink-0 text-slate-400" />
            <input
              v-model="pickerQuery"
              placeholder="Görsel adına göre ara (ör. haki, siyah, 5274)…"
              class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            />
          </div>
        </div>
        <div class="flex-1 overflow-y-auto p-5">
          <div
            v-if="pickerImages.length"
            class="grid grid-cols-4 sm:grid-cols-5 gap-3"
          >
            <button
              v-for="img in pickerImages"
              :key="img"
              class="aspect-square rounded-lg overflow-hidden border-2 transition"
              :class="
                picker.variant && picker.variant.image === img
                  ? 'border-woot-500'
                  : 'border-transparent hover:border-slate-300'
              "
              @click="chooseVariantImage(img)"
            >
              <img :src="img" class="w-full h-full object-cover" />
            </button>
          </div>
          <p
            v-else-if="media.images.length"
            class="text-sm text-slate-400 text-center py-8"
          >
            "{{ pickerQuery }}" ile eşleşen görsel yok.
          </p>
          <p v-else class="text-sm text-slate-400 text-center py-8">
            Bu ürünün galerisinde görsel yok. Aşağıdan yükleyebilirsin.
          </p>
        </div>
        <div
          class="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-slate-800"
        >
          <button
            class="text-xs text-red-500 font-medium"
            @click="clearVariantImage"
          >
            Görseli kaldır
          </button>
          <label
            class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 cursor-pointer"
          >
            <span
              :class="
                pickerUploading
                  ? 'i-lucide-loader-2 animate-spin'
                  : 'i-lucide-upload'
              "
              class="size-4"
            />
            Yeni görsel yükle
            <input
              type="file"
              accept="image/*"
              multiple
              class="hidden"
              @change="onPickerUpload"
            />
          </label>
        </div>
      </div>
    </div>

    <!-- 🛒 Ticimax'e Gönder modalı -->
    <div
      v-if="ticimax.open"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      @click.self="ticimax.open = false"
    >
      <div
        class="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 p-5 shadow-xl"
      >
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-bold text-slate-900 dark:text-slate-100">
            🛒 Ticimax'e Gönder
          </h2>
          <button
            class="text-slate-400 hover:text-slate-600"
            @click="ticimax.open = false"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">
          Ürün Ticimax'e <b>pasif</b> (yayında görünmez) gönderilir; Prapazar
          "Ticimax'ten import" ile kartı oradan çeker. Kategori, Prapazar'ın da
          kullanacağı Ticimax kataloğu kategorisidir.
        </p>
        <p
          v-if="ticimax.suggested && ticimax.catIds.length"
          class="text-xs font-semibold text-violet-700 bg-violet-50 dark:bg-slate-800 rounded-lg px-3 py-2 mb-2"
        >
          ✨ {{ ticimax.catIds.length }} kategori otomatik seçildi:
          {{ ticimax.suggested }} + üst menüleri + Yeni Ürünler. Listeden
          ekleyip çıkarabilirsin.
        </p>
        <input
          v-model="ticimax.catQuery"
          type="text"
          placeholder="Kategori ara… (örn. eşofman, bluz)"
          class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-2"
        />
        <div
          class="max-h-56 overflow-y-auto rounded-lg border border-slate-100 dark:border-slate-800 divide-y divide-slate-50 dark:divide-slate-800"
        >
          <button
            v-for="c in filteredCats"
            :key="c.id"
            class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
            :class="
              ticimax.catIds.includes(c.id)
                ? 'bg-woot-50 text-woot-700 font-semibold'
                : 'text-slate-700 dark:text-slate-300'
            "
            @click="toggleTicimaxCat(c.id)"
          >
            <span
              class="size-4 rounded border flex items-center justify-center text-[10px] shrink-0"
              :class="
                ticimax.catIds.includes(c.id)
                  ? 'bg-woot-500 border-woot-500 text-white'
                  : 'border-slate-300'
              "
            >
              {{ ticimax.catIds.includes(c.id) ? '✓' : '' }}
            </span>
            {{ c.ad }}
            <span class="text-xs text-slate-400">#{{ c.id }}</span>
          </button>
        </div>
        <p class="text-xs text-slate-500 mt-2">
          Seçili: {{ ticimax.catIds.length }} kategori (ilki ana kategori
          sayılır)
        </p>
        <p
          v-if="ticimax.msg"
          class="text-sm mt-3"
          :class="
            ticimax.msg.startsWith('✅') ? 'text-green-700' : 'text-red-600'
          "
        >
          {{ ticimax.msg }}
        </p>
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
            @click="ticimax.open = false"
          >
            Kapat
          </button>
          <button
            class="px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
            :disabled="ticimax.sending || !ticimax.catIds.length"
            @click="sendToTicimax"
          >
            {{
              ticimax.sending
                ? 'Gönderiliyor…'
                : ticimaxId
                  ? 'Güncelle (pasif)'
                  : 'Gönder (pasif)'
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- ⚡ Hızlı Stok Matrisi modalı: satır=renk, sütun=beden -->
    <div
      v-if="stokM.open"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      @click.self="stokM.open = false"
    >
      <div
        class="w-full max-w-3xl rounded-2xl bg-white dark:bg-slate-900 p-5 shadow-xl"
      >
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-bold text-slate-900 dark:text-slate-100">
            ⚡ Hızlı Stok Girişi
            <span class="text-sm font-normal text-slate-400">
              — toplam {{ stokMatrixTotal }} adet
            </span>
          </h2>
          <button
            class="text-slate-400 hover:text-slate-600"
            @click="stokM.open = false"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr>
                <th
                  class="text-left px-2 py-1.5 text-xs text-slate-500 font-semibold"
                >
                  Renk \ Beden
                </th>
                <th
                  v-for="sz in productSizes"
                  :key="sz"
                  class="px-1 py-1.5 text-xs text-slate-500 font-semibold text-center"
                >
                  {{ sz }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="renk in productColors"
                :key="renk"
                class="border-t border-slate-100 dark:border-slate-800"
              >
                <td
                  class="px-2 py-1.5 font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap"
                >
                  {{ renk }}
                </td>
                <td v-for="sz in productSizes" :key="sz" class="px-1 py-1">
                  <input
                    v-model.number="stokM.qty[stokKey(renk, sz)]"
                    type="number"
                    min="0"
                    class="w-14 px-1.5 py-1.5 text-sm text-center border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-woot-500"
                    @focus="$event.target.select()"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
            @click="stokM.open = false"
          >
            Vazgeç
          </button>
          <button
            class="px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600"
            @click="applyStokMatrix"
          >
            ✓ Stokları Uygula
          </button>
        </div>
      </div>
    </div>

    <!-- 📥 EXCEL'DEN RENK EKLE — fark önizlemesi.
         Kural: sadece EKSİK varyantlar eklenir; mevcut varyantlara, barkodlara
         ve kanal kimliklerine dokunulmaz, kartta olup Excel'de olmayan silinmez. -->
    <div
      v-if="renkEkleAcik"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
    >
      <div
        class="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 shadow-xl overflow-hidden"
      >
        <div
          class="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800"
        >
          <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100">
            📥 Excel'den Renk Ekle
            <span class="ml-1 font-normal text-slate-400">{{ parentCode }}</span>
          </h3>
          <button
            class="text-slate-400 hover:text-slate-600"
            @click="renkEkleAcik = false"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-5 py-4 text-sm">
          <div v-if="renkEkleMesgul && !renkEkleFark" class="text-slate-500 py-6 text-center">
            Excel okunuyor…
          </div>

          <div
            v-else-if="renkEkleHata"
            class="p-3 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400"
          >
            {{ renkEkleHata }}
          </div>

          <template v-else-if="renkEkleFark">
            <div class="grid grid-cols-3 gap-3 mb-4">
              <div class="rounded-xl bg-emerald-50 dark:bg-emerald-950 p-3">
                <div class="text-xl font-semibold text-emerald-700 dark:text-emerald-400">
                  {{ (renkEkleFark.yeni || []).length }}
                </div>
                <div class="text-xs text-emerald-700/70 dark:text-emerald-400/70">
                  eklenecek varyant
                </div>
              </div>
              <div class="rounded-xl bg-slate-50 dark:bg-slate-800 p-3">
                <div class="text-xl font-semibold text-slate-700 dark:text-slate-300">
                  {{ (renkEkleFark.mevcut || []).length }}
                </div>
                <div class="text-xs text-slate-500">zaten kartta</div>
              </div>
              <div class="rounded-xl bg-slate-50 dark:bg-slate-800 p-3">
                <div class="text-xl font-semibold text-slate-700 dark:text-slate-300">
                  {{ (renkEkleFark.karttaFazla || renkEkleFark.kartta_fazla || []).length }}
                </div>
                <div class="text-xs text-slate-500">Excel'de yok (silinmez)</div>
              </div>
            </div>

            <div
              v-if="!(renkEkleFark.yeni || []).length"
              class="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
            >
              Bu Excel'de karta eklenecek yeni varyant yok — kart zaten güncel.
            </div>

            <div v-else>
              <div class="flex items-center justify-between mb-2">
                <div class="font-medium text-slate-700 dark:text-slate-200">
                  Hangi renkleri alıyorsunuz?
                </div>
                <div class="flex items-center gap-2 text-xs">
                  <button
                    class="px-2 py-1 rounded-lg text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950 font-medium"
                    @click="tumunuSec"
                  >
                    Tümünü seç
                  </button>
                  <button
                    v-if="secilenRenkler.length"
                    class="px-2 py-1 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                    @click="secimiTemizle"
                  >
                    Temizle
                  </button>
                </div>
              </div>
              <p class="text-xs text-slate-500 mb-3">
                Adrom üretim planlamadaki tüm renkler listede görünür; yalnızca
                aldıklarınızı işaretleyin.
              </p>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div
                  v-for="r in renkOzetleri"
                  :key="r.renk"
                  class="rounded-xl border transition overflow-hidden"
                  :class="
                    secilenRenkler.includes(r.renk)
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/40 ring-1 ring-violet-500'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  "
                >
                  <button
                    type="button"
                    class="w-full flex items-center gap-3 p-3 text-left"
                    @click="renkSec(r.renk)"
                  >
                    <span
                      class="size-8 rounded-lg shrink-0 border border-black/10 shadow-inner"
                      :style="{ background: renkNoktasi(r.renk) }"
                    />
                    <span class="flex-1 min-w-0">
                      <span
                        class="block font-medium text-slate-800 dark:text-slate-100 truncate"
                      >
                        {{ r.renk }}
                      </span>
                      <span class="block text-xs text-slate-500">
                        {{ r.adet }} beden · kod {{ r.renkKodu || r.renk_kodu }}
                        <template v-if="r.barkodlu < r.adet">
                          · <span class="text-amber-600">{{ r.adet - r.barkodlu }} barkodsuz</span>
                        </template>
                      </span>
                    </span>
                    <span
                      class="size-5 rounded-md shrink-0 flex items-center justify-center border"
                      :class="
                        secilenRenkler.includes(r.renk)
                          ? 'bg-violet-600 border-violet-600 text-white'
                          : 'border-slate-300 dark:border-slate-600'
                      "
                    >
                      <span v-if="secilenRenkler.includes(r.renk)" class="i-lucide-check size-3.5" />
                    </span>
                  </button>

                  <!-- Seçilen rengin görselleri: kanala SADECE bu renkle birlikte
                       gider; diğer renklerin kanaldaki fotoğraflarına dokunulmaz. -->
                  <div
                    v-if="secilenRenkler.includes(r.renk)"
                    class="px-3 pb-3 pt-1 border-t border-violet-200 dark:border-violet-900"
                  >
                    <div class="flex items-center gap-2 flex-wrap">
                      <div
                        v-for="u in renkGorselleri[r.renk] || []"
                        :key="u"
                        class="relative size-12 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 group"
                      >
                        <img :src="u" class="size-full object-cover" alt="" />
                        <button
                          type="button"
                          class="absolute inset-0 bg-black/60 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center"
                          @click="renkGorselSil(r.renk, u)"
                        >
                          <span class="i-lucide-trash-2 size-4" />
                        </button>
                      </div>

                      <label
                        class="size-12 rounded-lg border-2 border-dashed border-violet-300 dark:border-violet-800 flex items-center justify-center cursor-pointer hover:border-violet-500 text-violet-500"
                        :title="`${r.renk} için görsel yükle`"
                      >
                        <span
                          v-if="renkYukleniyor === r.renk"
                          class="i-lucide-loader-2 size-4 animate-spin"
                        />
                        <span v-else class="i-lucide-image-plus size-4" />
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          class="hidden"
                          @change="renkGorselSec($event, r.renk)"
                        />
                      </label>

                      <span
                        v-if="!(renkGorselleri[r.renk] || []).length"
                        class="text-xs text-slate-500"
                      >
                        Bu rengin fotoğraflarını yükleyin
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="secilenRenkler.length"
                class="mt-3 text-sm text-slate-600 dark:text-slate-300"
              >
                Seçilen:
                <span class="font-semibold text-violet-600 dark:text-violet-400">
                  {{ secilenRenkler.join(', ') }}
                </span>
                — toplam {{ secilenVaryantSayisi }} varyant eklenecek.
              </div>

              <div
                v-if="secilenRenkler.length && gorselsizSecili.length"
                class="mt-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400 text-xs"
              >
                Görseli yüklenmemiş renk:
                <b>{{ gorselsizSecili.join(', ') }}</b>. Şimdi yüklemezseniz
                varyantlar eklenir ama kanala fotoğrafsız gider; sonradan Renk
                Stüdyosu'ndan da ekleyebilirsiniz.
              </div>
            </div>

            <div
              v-if="(renkEkleFark.excelTekrarliBarkod || renkEkleFark.excel_tekrarli_barkod || []).length"
              class="mt-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400 text-xs"
            >
              ⚠️ Excel'de aynı barkod birden fazla satırda:
              {{ (renkEkleFark.excelTekrarliBarkod || renkEkleFark.excel_tekrarli_barkod || [])
                  .map(t => t.barkod + ' (' + t.adet + ')').join(', ') }}.
              Adrom tarafında kontrol edilmeli.
            </div>

            <div
              v-if="renkEkleFark.barcodeMissing || renkEkleFark.barcode_missing"
              class="mt-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400 text-xs"
            >
              ⚠️ {{ renkEkleFark.barcodeMissing || renkEkleFark.barcode_missing }} satırın barkodu
              Excel'de boş. Barkodları tamamlamadan kanallara göndermeyin.
            </div>

            <p class="mt-4 text-xs text-slate-500">
              Ekledikten sonra yeni renk Renk Stüdyosu'nda görünür; görsellerini
              oraya sürükleyip kaydedin, sonra kanala gönderin. Mevcut renklerin
              görselleri kanala ikinci kez gitmez.
            </p>
          </template>
        </div>

        <div
          class="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 dark:border-slate-800"
        >
          <button
            class="px-4 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            @click="renkEkleAcik = false"
          >
            Vazgeç
          </button>
          <button
            v-if="renkEkleFark && (renkEkleFark.yeni || []).length"
            :disabled="renkEkleMesgul || !secilenRenkler.length"
            class="px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
            @click="renkEkleUygula"
          >
            <template v-if="renkEkleMesgul">Ekleniyor…</template>
            <template v-else-if="!secilenRenkler.length">Renk seçin</template>
            <template v-else>✓ {{ secilenVaryantSayisi }} varyantı ekle</template>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
